import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import AdminShell from "@/components/admin/AdminShell";
import AdminOverviewDashboard from "@/components/admin/AdminOverviewDashboard";
import AdminCatalogBroadcastCard from "@/components/admin/AdminCatalogBroadcastCard";
import { ADMIN_TAB_ICONS } from "@/lib/navIcons";
import AdminTransactionsTab from "@/components/admin/AdminTransactionsTab";
import AdminListShell, { AdminListRow } from "@/components/admin/AdminListShell";
import AdminDetailDialog from "@/components/admin/AdminDetailDialog";
import AdminUserDetailPanel, { AdminUserDetailToolbar } from "@/components/admin/AdminUserDetailPanel";
import ModalDossierEmbed from "@/components/shared/ModalDossierEmbed";
import AnnonceEditDialog from "@/components/shared/AnnonceEditDialog";
import { api, fetchAdminCallRecording, isAdminProfileDeleted } from "@/lib/api";
import { supabase } from "@/lib/supabase";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { messageCoordonneesInterdites, texteContientCoordonnees } from "@/lib/contactGuard";
import { adminSuggestionKind, buildAdminSuggestionMailto } from "@/lib/adminSuggestionMailto";
import { formatPhoneForDisplay } from "@/lib/phoneFormat";
import { profileDisplayName } from "@/lib/profileDisplayName";
import {
  aiCallClosureDecisionHeadlineFr,
  litigeDecisionExplainedFr,
  litigeDecisionShortFr,
} from "@/lib/litigeDecisionLabels";
import {
  litigeRowStatusLabelFr,
  adminUserSubscriptionStatusForList,
  isAdminUserSubscriptionChurned,
  profileRoleLabelFr,
  negotiationStatusLabelFr,
  dossierStatusLabelFr,
} from "@/lib/marketplaceStatusLabels";
import { SELLER_PAYOUT_HOURS } from "@/lib/businessDays";
import { computeAdminSellerQuality, computeAdminBuyerQuality } from "@/lib/adminSellerQuality";
import { cn } from "@/lib/utils";
import { normalizeAnnonceType } from "@/lib/annonceEdit";
import { getDeptDisplayLabel } from "@/lib/departementDisplay";
import {
  Trash2,
  Pencil,
  Plus,
  Copy,
  GripVertical,
  PauseCircle,
  Tag,
  Search,
  X,
  SlidersHorizontal,
  EyeOff,
  Eye,
  Mail,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  ArrowLeft,
  AlertTriangle,
  ShieldCheck,
  Scale,
  Users,
  TrendingUp,
  LineChart,
  LayoutDashboard,
  Wallet,
} from "lucide-react";

/** Libellé statut transaction (cohérent avec le tableau de bord). */
function adminTxStatutLabelFr(st) {
  const s = String(st || "").trim().toLowerCase();
  const map = {
    paye: "Séquestre (payé)",
    appel_en_cours: "Appel en cours",
    appel_effectue: "Appel effectué",
    fonds_debloques: "RDV confirmé",
    rembourse: "Remboursé",
    annule: "Annulé",
  };
  if (map[s]) return map[s];
  return s ? String(st).trim() : "—";
}

function formatAdminDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
}

/** Initiales pour avatar liste admin utilisateurs. */
function adminUserInitials(displayName) {
  const s = String(displayName || "").trim();
  if (!s) return "?";
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    const a = parts[0][0];
    const b = parts[parts.length - 1][0];
    if (a && b) return `${a}${b}`.toUpperCase();
  }
  return s.slice(0, 2).toUpperCase();
}

function activityKindLabelFr(kind) {
  const k = String(kind || "").trim();
  const map = {
    account: "Compte",
    dossier: "Annonce",
    transaction: "Transaction",
    transaction_update: "Transaction",
    negotiation: "Négociation",
    litige_open: "Litige",
    litige_resolve: "Litige",
    notation: "Note",
  };
  return map[k] || k || "—";
}

/** Lien profil — en modale admin : ouverture interne ; sinon lien public. */
function AdminPeerLink({ userId, label, onOpenUser }) {
  if (!userId || !label) return <span className="text-slate-400">—</span>;
  if (onOpenUser) {
    return (
      <button
        type="button"
        className="font-medium text-[#2D7FF9] hover:underline text-left"
        onClick={(e) => {
          e.stopPropagation();
          onOpenUser(userId);
        }}
      >
        {label}
      </button>
    );
  }
  return (
    <Link
      to={`/profil/${userId}`}
      className="font-medium text-[#2D7FF9] hover:underline"
      onClick={(e) => e.stopPropagation()}
    >
      {label}
    </Link>
  );
}

function csvEscapeCell(v) {
  const s =
    v == null
      ? ""
      : typeof v === "object"
        ? JSON.stringify(v)
        : String(v);
  return `"${s.replace(/"/g, '""')}"`;
}

function csvRow(cells) {
  return cells.map(csvEscapeCell).join(",");
}

/** Couleur secteur en base (colonne existante) — plus affichée à l’admin, valeur fixe. */
const SECTEUR_COULEUR_DEFAUT = "#2D7FF9";

/** Garantit un tableau (évite TypeError sur .filter/.map si l’API renvoie { items: [] } etc.). */
function asList(v) {
  if (Array.isArray(v)) return v;
  if (v && typeof v === "object" && Array.isArray(v.items)) return v.items;
  if (v && typeof v === "object" && Array.isArray(v.data)) return v.data;
  return [];
}

const PRODUIT_CHAMP_TYPES = ["text", "number", "list", "boolean"];
const PRODUIT_CHAMP_TYPE_SET = new Set(PRODUIT_CHAMP_TYPES);

const CIBLES_ANNONCE = [
  { value: "tous", label: "Vente & recherche" },
  { value: "vente", label: "Vente uniquement" },
  { value: "recherche", label: "Recherche uniquement" },
];

function labelProduitChampType(t) {
  if (t === "text") return "Texte";
  if (t === "number") return "Nombre";
  if (t === "list") return "Menu déroulant";
  if (t === "boolean") return "Oui / Non";
  return t || "Texte";
}

/** Options menu déroulant : ne garde que les lignes non vides pour l’API. */
function sanitizeListOptionsForSave(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.map((s) => String(s).trim()).filter(Boolean);
}

/** Au moins une ligne éditable ; si la base est vide, une ligne vide pour saisir. */
function listOptionsToRows(options) {
  if (!Array.isArray(options) || options.length === 0) return [""];
  return options.map((o) => String(o));
}

function normalizeChampForForm(c) {
  const t = c?.type && PRODUIT_CHAMP_TYPE_SET.has(c.type) ? c.type : "text";
  const cible = c?.cible_annonces;
  const cible_annonces = cible === "vente" || cible === "recherche" ? cible : "tous";
  return {
    ...c,
    nom: c?.nom != null ? String(c.nom) : "",
    type: t,
    cible_annonces,
    options: Array.isArray(c?.options) ? c.options : [],
    required: Boolean(c?.required),
    visible_public: c?.visible_public !== false,
  };
}

function moveChampInList(champs, from, to) {
  if (!Array.isArray(champs) || from < 0 || to < 0 || from >= champs.length || to >= champs.length) {
    return champs;
  }
  if (from === to) return champs;
  const next = [...champs];
  const [x] = next.splice(from, 1);
  next.splice(to, 0, x);
  return next;
}

function formatMontant(n) {
  if (n == null || n === "" || Number.isNaN(Number(n))) return "—";
  return `${Number(n).toLocaleString("fr-FR")}€`;
}

function disputeTransactionAnchorMs(d) {
  const t = d?.transaction;
  const raw = t?.capture_confirmed_at ?? t?.updated_at;
  if (!raw) return null;
  const ms = Date.parse(String(raw));
  return Number.isFinite(ms) ? ms : null;
}

/** Fin de la fenêtre « 72 h » alignée sur la clôture RDV (même ancre que l’ouverture litige côté acheteur). */
function disputeWindowEndMs(d, hours = SELLER_PAYOUT_HOURS) {
  const h = Math.max(0, Number(hours) || 72);
  const anchor = disputeTransactionAnchorMs(d);
  if (anchor != null) return anchor + h * 3600000;
  const opened = d?.created_at ? Date.parse(String(d.created_at)) : NaN;
  return Number.isFinite(opened) ? opened + h * 3600000 : Number.POSITIVE_INFINITY;
}

function isLitigeOpenRow(d) {
  const s = String(d?.status ?? d?.statut ?? "").trim().toLowerCase();
  return s === "open" || s === "in_review";
}

/** Durée lisible (j / h / min), pour temps restant ou écoulé. */
function formatDureeFr(msAbs) {
  const abs = Math.max(0, Math.floor(msAbs));
  if (abs < 60000) return "< 1 min";
  const j = Math.floor(abs / 86400000);
  const h = Math.floor((abs % 86400000) / 3600000);
  const m = Math.floor((abs % 3600000) / 60000);
  const parts = [];
  if (j) parts.push(`${j} j`);
  if (h) parts.push(`${h} h`);
  parts.push(`${m} min`);
  return parts.join(" ");
}

const LITIGE_TEMPS_FENETRE_DATE_OPTS = {
  day: "2-digit",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
};

/**
 * Texte + tonalité pour chaque ligne (temps restant si ouvert, sinon date de fin de fenêtre).
 */
function litigeLigneTempsFenetre72h(d) {
  const end = disputeWindowEndMs(d);
  if (!Number.isFinite(end) || end === Number.POSITIVE_INFINITY) {
    return {
      text: "Fenêtre 72 h — échéance inconnue (date de clôture RDV absente)",
      tone: "unknown",
    };
  }
  const endLabel = new Date(end).toLocaleString("fr-FR", LITIGE_TEMPS_FENETRE_DATE_OPTS);
  if (!isLitigeOpenRow(d)) {
    return { text: `Fin fenêtre 72 h : ${endLabel}`, tone: "closed" };
  }
  const rem = end - Date.now();
  const abs = Math.abs(rem);
  const dur = formatDureeFr(abs);
  if (rem >= 0) {
    return { text: `Temps restant avant fin fenêtre : ${dur}`, tone: "remaining" };
  }
  return {
    text: `Fenêtre 72 h dépassée depuis ${dur} · fin prévue ${endLabel}`,
    tone: "overdue",
  };
}

function ownerDisplay(d) {
  const name = d?.seller_name || d?.owner_name || d?.vendeur_name || d?.seller?.name || d?.profiles?.name || null;
  const email = d?.seller_email || d?.owner_email || d?.vendeur_email || d?.seller?.email || d?.profiles?.email || null;
  const phone = d?.seller_phone || d?.owner_phone || d?.vendeur_phone || d?.seller?.phone || d?.profiles?.phone || null;
  return { name, email, phone };
}

/** Une seule ligne lisible (nom + e-mail + tél.) sans UUID. */
function adminOwnerLine(o) {
  const bits = [];
  if (o.name) bits.push(String(o.name).trim());
  if (o.email) {
    const em = String(o.email).trim();
    if (em && !bits.includes(em)) bits.push(em);
  }
  if (o.phone) {
    const ph = formatPhoneForDisplay(o.phone);
    if (ph && !bits.includes(ph)) bits.push(ph);
  }
  return bits.length ? bits.join(" · ") : "Inconnu";
}

function blockPreanalyseIa(d) {
  const raw = d.pre_analyse_ia_litige ?? d.ai_pre_analysis;
  if (raw == null) return null;
  if (typeof raw === "string") {
    return (
      <div className="bg-[#2D7FF9]/5 border border-[#2D7FF9]/20 rounded p-3 text-xs">
        <div className="font-semibold mb-1">Pré-analyse IA (litige)</div>
        <div className="whitespace-pre-wrap">{raw}</div>
      </div>
    );
  }
  if (typeof raw === "object" && !Array.isArray(raw)) {
    return (
      <div className="bg-[#2D7FF9]/5 border border-[#2D7FF9]/20 rounded p-3 text-xs">
        <div className="font-semibold mb-1">Pré-analyse IA (litige)</div>
        {raw.reasoning != null ? <div className="mb-1">{String(raw.reasoning)}</div> : null}
        <div className="text-slate-500">Détournement : {raw.diversion_detected ? "Oui" : "Non"} · Confiance : {Math.round((raw.confidence || 0) * 100)}%</div>
      </div>
    );
  }
  return null;
}

function formatStars(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return n.toFixed(1);
}

function identityLine({ name, email, rating }) {
  const parts = [];
  if (name) parts.push(String(name).trim());
  if (email) {
    const em = String(email).trim();
    if (em && !parts.includes(em)) parts.push(em);
  }
  if (!parts.length) {
    parts.push("Identité indisponible (profil ou e-mail absent, ou droits RLS sur `profiles`).");
  }
  const star = formatStars(rating);
  parts.push(`⭐ ${star}`);
  return parts.join(" · ");
}

/** Barre de recherche réutilisable sur les listes admin. */
function AdminSearchBar({ id, testId, placeholder, value, onChange, count, total, countNoun = "résultat" }) {
  const hasFilter = Boolean(String(value || "").trim());
  const noun = count === 1 ? countNoun : `${countNoun}s`;
  return (
    <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
      <div className="relative min-w-0 flex-1 sm:max-w-lg">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
          aria-hidden
        />
        <Input
          id={id}
          data-testid={testId}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          className="h-10 border-slate-200/90 bg-white pl-9 shadow-sm focus-visible:ring-sky-500/25"
          aria-label={placeholder}
        />
      </div>
      {typeof count === "number" ? (
        <div className="flex shrink-0 flex-wrap items-center gap-2 text-xs text-slate-600">
          <Badge variant="secondary" className="font-medium tabular-nums text-slate-700">
            {hasFilter && typeof total === "number"
              ? `${count} / ${total} ${noun}`
              : `${count} ${noun}`}
          </Badge>
          {hasFilter ? <span className="text-slate-500">Filtre actif</span> : null}
        </div>
      ) : null}
    </div>
  );
}

/** Retire le bloc « Synthèse IA » en fin de message (déjà repris structuré à droite). */
function stripLitigeSynthèseIaBlock(description) {
  const raw = String(description || "").trim();
  if (!raw) return "";
  const idx = raw.search(/\s*Synthèse IA\s*:/i);
  if (idx < 0) return raw;
  const head = raw.slice(0, idx).trim();
  return head.length ? head : raw;
}

/** Extrait le texte après « Synthèse IA : » dans le message litige (secours si `ai_analysis` incomplet). */
function extractSynthèseIaFromLitigeMessage(raw) {
  const s = String(raw || "");
  const idx = s.search(/\bSynthèse IA\s*:/i);
  if (idx < 0) return "";
  let tail = s.slice(idx).replace(/^[\s\S]*?\bSynthèse IA\s*:\s*/i, "").trim();
  const stop = tail.search(/\n\n(?:Mon compte rendu|Je demande)/i);
  if (stop > 0) tail = tail.slice(0, stop).trim();
  return tail;
}

function IaIndicator({ label, value, tone = "neutral" }) {
  const toneClass =
    tone === "yes"
      ? "border-emerald-200 bg-emerald-50 text-emerald-900"
      : tone === "alert"
        ? "border-amber-200 bg-amber-50 text-amber-900"
        : "border-slate-200 bg-slate-50 text-slate-800";
  return (
    <div className={`min-w-0 rounded-lg border px-2.5 py-2 ${toneClass}`}>
      <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-0.5 break-words text-sm font-semibold tabular-nums">{value}</p>
    </div>
  );
}

function blockSyntheseTransaction(ia, opts = {}) {
  const litigeMessageRaw = opts?.litigeMessageRaw != null ? String(opts.litigeMessageRaw) : "";

  if (ia == null) {
    return (
      <p className="text-sm text-slate-500">
        Aucune analyse enregistrée (appel non clôturé côté téléphonie ou synthèse indisponible).
      </p>
    );
  }
  if (typeof ia === "string") {
    const t = ia.trim();
    return t ? (
      <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{t}</p>
    ) : (
      <p className="text-sm text-slate-500">—</p>
    );
  }

  const obj = ia && typeof ia === "object" && !Array.isArray(ia) ? ia : null;
  if (!obj) {
    return <p className="text-sm text-slate-500">Synthèse indisponible (format non reconnu).</p>;
  }

  const harvest = obj.harvest_analysis && typeof obj.harvest_analysis === "object" ? obj.harvest_analysis : null;
  const refusalNested =
    obj.refusal_analysis && typeof obj.refusal_analysis === "object" ? obj.refusal_analysis : null;
  const refusal = refusalNested || (obj.refusal_analysis ? obj : null);

  const decisionHeadline = aiCallClosureDecisionHeadlineFr(obj.decision);
  const explanationRaw =
    (refusal && (refusal.reason || refusal.reasoning)) ||
    (harvest && harvest.reason) ||
    obj.reason ||
    obj.reasoning ||
    extractSynthèseIaFromLitigeMessage(litigeMessageRaw) ||
    null;
  const explanation = String(explanationRaw || "").trim();

  const confidenceSrc =
    refusal?.confidence != null ? refusal.confidence : harvest?.confidence != null ? harvest.confidence : obj.confidence;
  const confidencePct =
    confidenceSrc != null && Number.isFinite(Number(confidenceSrc))
      ? `${Math.round(Number(confidenceSrc) * 100)} %`
      : null;

  const indicators = [];
  if (refusal && typeof refusal.legitimate_refusal === "boolean") {
    indicators.push({
      key: "refusal",
      label: "Refus fondé",
      value: refusal.legitimate_refusal ? "Oui" : "Non",
      tone: refusal.legitimate_refusal ? "yes" : "neutral",
    });
  }
  if (refusal && typeof refusal.diversion_detected === "boolean") {
    indicators.push({
      key: "diversion",
      label: "Détournement",
      value: refusal.diversion_detected ? "Oui" : "Non",
      tone: refusal.diversion_detected ? "alert" : "neutral",
    });
  }
  const harvestViolation =
    harvest && typeof harvest.contact_harvest_violation === "boolean"
      ? harvest.contact_harvest_violation
      : typeof obj.contact_harvest_violation === "boolean"
        ? obj.contact_harvest_violation
        : null;
  if (harvestViolation != null) {
    indicators.push({
      key: "harvest",
      label: "Anti-contournement",
      value: harvestViolation ? "Violation" : "Conforme",
      tone: harvestViolation ? "alert" : "yes",
    });
  }
  if (confidencePct) {
    indicators.push({ key: "confidence", label: "Confiance IA", value: confidencePct, tone: "neutral" });
  }

  const platformRule =
    typeof obj.platform_rule_violation === "string" ? String(obj.platform_rule_violation).trim() : "";

  const hasContent = decisionHeadline || explanation || indicators.length > 0 || platformRule;

  if (!hasContent) {
    return <p className="text-sm text-slate-500">Synthèse enregistrée sans détail exploitable.</p>;
  }

    return (
    <div className="min-w-0 max-w-full space-y-3">
      {decisionHeadline ? (
        <div className="rounded-lg border border-[#2D7FF9]/25 bg-[#2D7FF9]/5 px-3 py-2.5">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-[#0B3F8C]/75">
            Décision automatique (appel)
          </p>
          <p className="mt-1 text-sm font-medium leading-snug text-slate-900">{decisionHeadline}</p>
        </div>
      ) : null}
      {explanation ? (
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
            Explication de l’analyse
          </p>
          <p className="text-sm text-slate-800 leading-relaxed whitespace-pre-wrap">{explanation}</p>
        </div>
      ) : decisionHeadline || indicators.length > 0 ? (
        <p className="text-sm text-slate-500 italic">
          Aucun texte détaillé enregistré pour cette analyse (seule la décision et les indicateurs sont disponibles).
        </p>
      ) : null}
      {indicators.length > 0 ? (
        <div className="grid min-w-0 grid-cols-2 gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {indicators.map(({ key, label, value, tone }) => (
            <IaIndicator key={key} label={label} value={value} tone={tone} />
          ))}
        </div>
      ) : null}
      {platformRule ? (
        <p className="text-xs text-slate-600">
          <span className="font-semibold text-slate-700">Règle plateforme :</span> {platformRule}
        </p>
      ) : null}
      </div>
    );
  }


/** Contenu détaillé d’un litige (liste « Litiges » dépliée ou panneau latéral sans changer d’onglet). */
function AdminLitigeExpandedBody({
  d,
  recordingLoadTid,
  onListenRecording,
  onOpenRefund,
  onOpenRelease,
  onViewDossier,
  sheet,
}) {
  const litigePk = d.id || d.dispute_id;
  const rawLitigeText = String(d.message_partie || d.description || "").trim();
  const messagePlateforme = stripLitigeSynthèseIaBlock(rawLitigeText).trim() || "—";

  const prixDealVal = d.prix_deal != null ? d.prix_deal : d.prix_vente_transaction;
  const departN =
    d.prix_depart != null && d.prix_depart !== "" && !Number.isNaN(Number(d.prix_depart)) ? Number(d.prix_depart) : null;
  const dealN =
    prixDealVal != null && prixDealVal !== "" && !Number.isNaN(Number(prixDealVal)) ? Number(prixDealVal) : null;
  const montantsIdentiques = departN != null && dealN != null && departN === dealN;

  return (
    <div
      className={
        sheet
          ? "min-w-0 max-w-full space-y-4 pt-1"
          : "min-w-0 max-w-full space-y-4 border-t border-[#E5E7EB] bg-slate-50/40 px-4 pb-4 pt-4 sm:px-5"
      }
    >
      <div className="grid min-w-0 grid-cols-1 gap-3 text-sm sm:grid-cols-2">
        <div className="min-w-0 rounded border border-slate-200 bg-slate-50/80 p-3">
          <div className="text-[10px] uppercase text-slate-500 font-semibold">Acheteur</div>
          <div className="mt-1 break-words text-slate-800">
            {identityLine({ name: d.acheteur_name, email: d.acheteur_email, rating: d.acheteur_rating_avg })}
          </div>
        </div>
        <div className="min-w-0 rounded border border-slate-200 bg-slate-50/80 p-3">
          <div className="text-[10px] uppercase text-slate-500 font-semibold">Vendeur</div>
          <div className="mt-1 break-words text-slate-800">
            {identityLine({ name: d.vendeur_name, email: d.vendeur_email, rating: d.vendeur_rating_avg })}
          </div>
        </div>
      </div>

      <div
        className={`grid min-w-0 max-w-full grid-cols-1 gap-2 text-sm border-t border-[#E5E7EB] pt-3 ${montantsIdentiques ? "sm:grid-cols-2" : "sm:grid-cols-2 lg:grid-cols-3"}`}
      >
        {montantsIdentiques ? (
          <div className="sm:col-span-1">
            <span className="text-slate-500">Montant :</span>{" "}
            <span className="font-semibold tabular-nums">{formatMontant(departN)}</span>
            <span className="ml-1.5 text-xs font-normal text-slate-400">(annonce = transaction)</span>
          </div>
        ) : (
          <>
            <div>
              <span className="text-slate-500">Montant départ :</span>{" "}
              <span className="font-semibold tabular-nums">{formatMontant(d.prix_depart)}</span>
            </div>
            <div>
              <span className="text-slate-500">Montant deal :</span>{" "}
              <span className="font-semibold tabular-nums">{formatMontant(prixDealVal)}</span>
            </div>
          </>
        )}
        <div className={`truncate ${montantsIdentiques ? "sm:col-span-1 sm:text-right" : ""}`}>
          <span className="text-slate-500">Annonce :</span> {d.dossier?.title || "—"}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {d.dossier?.dossier_id && onViewDossier ? (
          <Button type="button" size="sm" variant="outline" onClick={() => onViewDossier(d.dossier.dossier_id)}>
            Voir la fiche annonce
          </Button>
        ) : null}
        {d.transaction_id ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={recordingLoadTid === String(d.transaction_id)}
            onClick={() => onListenRecording(d.transaction_id)}
          >
            {recordingLoadTid === String(d.transaction_id) ? "Chargement…" : "Écouter l’enregistrement"}
          </Button>
        ) : null}
      </div>

      <div className="min-w-0 max-w-full rounded-lg border border-slate-200 bg-white p-4 shadow-sm space-y-4">
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1.5">
            Message du litige
          </div>
          <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{messagePlateforme}</p>
        </div>
        <div className="border-t border-slate-100 pt-4">
          <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mb-1.5">
            Analyse IA (clôture appel)
          </div>
          {blockSyntheseTransaction(d.synthese_ia_transaction, { litigeMessageRaw: rawLitigeText }) || (
            <p className="text-xs text-slate-500">—</p>
          )}
        </div>
      </div>

      {blockPreanalyseIa(d)}

      {d.status === "closed" ? (
        <div className="rounded border border-slate-200 bg-slate-50/80 p-3 text-sm">
          <div className="text-[10px] uppercase text-slate-500 font-semibold">Clôture admin</div>
          <p className="mt-1">
            <span className="text-slate-500">Décision :</span>{" "}
            <span className="font-semibold">{litigeDecisionShortFr(d.admin_decision)}</span>
          </p>
          <p className="mt-1.5 text-xs text-slate-600 leading-snug">{litigeDecisionExplainedFr(d.admin_decision)}</p>
          <p className="mt-2 text-[10px] uppercase text-slate-500 font-semibold">Message à la partie défavorisée</p>
          <p className="text-sm text-slate-800 whitespace-pre-wrap mt-0.5">
            {String(d.resolution_message || "").trim() || "—"}
          </p>
        </div>
      ) : null}

      {(d.status === "open" || d.status === "in_review") && (
        <div className="min-w-0 max-w-full space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Décision</p>
          <div className="grid min-w-0 grid-cols-1 gap-2 sm:grid-cols-2">
            <Button
              type="button"
              className="h-11 w-full rounded-lg bg-[#2D7FF9] text-sm font-semibold text-white shadow-sm hover:bg-[#2568E8]"
              onClick={() => onOpenRefund(litigePk)}
              data-testid={`refund-${litigePk}`}
            >
              Rembourser l’acheteur
            </Button>
            <Button
              type="button"
              className="h-11 w-full rounded-lg bg-[#2D7FF9] text-sm font-semibold text-white shadow-sm hover:bg-[#2568E8]"
              onClick={() => onOpenRelease(litigePk)}
              data-testid={`release-${litigePk}`}
            >
              Verser au vendeur
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

const ADMIN_TAB_VALUES = new Set([
  "overview",
  "pending",
  "diffusion",
  "disputes",
  "transactions",
  "users",
  "produits",
  "secteurs",
  "contact",
]);

const ADMIN_PAGE_META = {
  overview: {
    title: "Vue d'ensemble",
    subtitle: "Pilotage chiffré, urgences et accès directs aux onglets concernés.",
  },
  pending: {
    title: "Modération",
    subtitle: "Annonces en ligne — consulter, modifier, masquer ou remettre sur la marketplace",
  },
  diffusion: {
    title: "Diffusion",
    subtitle: "Message catalogue à copier-coller (secteur, produits, lien rdvmarket.fr)",
  },
  disputes: {
    title: "Litiges",
    subtitle: "Ouverts en priorité, selon la fenêtre de déblocage vendeur",
  },
  transactions: {
    title: "Transactions",
    subtitle: "Flux financiers, délais de versement, litiges et alertes Stripe",
  },
  contact: {
    title: "Contact",
    subtitle: "Messages envoyés via « Nous écrire »",
  },
  users: {
    title: "Utilisateurs",
    subtitle: "Comptes, modération, historique et actions",
  },
  produits: {
    title: "Produits",
    subtitle: "Catalogue et champs dynamiques des annonces",
  },
  secteurs: {
    title: "Secteurs",
    subtitle: "Catégories du marketplace",
  },
};

const Admin = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = useMemo(() => {
    const raw = searchParams.get("tab");
    const t = raw === "suggestions" ? "contact" : raw;
    if (t && ADMIN_TAB_VALUES.has(t)) return t;
    return "overview";
  }, [searchParams]);

  const focusedLitigeId = useMemo(() => String(searchParams.get("litige") || "").trim(), [searchParams]);
  /** Litige ouvert en panneau latéral depuis un autre onglet (sans changer `tab`). */
  const [adminLitigeDetailId, setAdminLitigeDetailId] = useState(null);
  const [adminSuggestionDetailId, setAdminSuggestionDetailId] = useState(null);
  const [adminPendingDetailId, setAdminPendingDetailId] = useState(null);
  const [adminEditDossier, setAdminEditDossier] = useState(null);
  const [adminDossierEmbedKey, setAdminDossierEmbedKey] = useState(0);
  const [adminProduitDetailId, setAdminProduitDetailId] = useState(null);
  const [adminSecteurDetailId, setAdminSecteurDetailId] = useState(null);
  const [adminUserPane, setAdminUserPane] = useState("main");
  const [adminUserPaneId, setAdminUserPaneId] = useState(null);
  const [adminLitigePane, setAdminLitigePane] = useState("main");
  const [adminLitigePaneDossierId, setAdminLitigePaneDossierId] = useState(null);
  /** Retour depuis la modale litige (ex. fiche transaction ouverte en dessous). */
  const [adminLitigeReturnTo, setAdminLitigeReturnTo] = useState(null);
  /** Retour depuis la modale utilisateur (ex. fiche transaction). */
  const [adminUserReturnTo, setAdminUserReturnTo] = useState(null);
  const adminUsersFilter = useMemo(() => {
    const f = String(searchParams.get("usersFilter") || "").trim().toLowerCase();
    if (f === "churned" || f === "deleted" || f === "suspended") return f;
    return "all";
  }, [searchParams]);

  const adminUserFromUrl = useMemo(() => String(searchParams.get("user") || "").trim(), [searchParams]);

  const setAdminUsersFilter = useCallback(
    (filter) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("tab", "users");
          const f = String(filter || "all").trim().toLowerCase();
          if (f === "all") next.delete("usersFilter");
          else next.set("usersFilter", f);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setActiveTab = useCallback(
    (value, opts) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value === "overview") next.delete("tab");
          else if (value === "pending") next.set("tab", "pending");
          else next.set("tab", value);
          if (value !== "disputes") next.delete("litige");
          if (value === "users" && opts?.usersFilter) {
            next.set("usersFilter", String(opts.usersFilter).trim().toLowerCase());
          } else if (value !== "users") {
            next.delete("usersFilter");
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const clearAdminLitigeFromUrl = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("litige");
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const closeAdminLitigeModal = useCallback(() => {
    setAdminLitigeDetailId(null);
    setAdminLitigePane("main");
    setAdminLitigePaneDossierId(null);
    setAdminLitigeReturnTo(null);
    clearAdminLitigeFromUrl();
  }, [clearAdminLitigeFromUrl]);

  const handleAdminLitigeBack = useCallback(() => {
    if (adminLitigePane === "dossier") {
      setAdminLitigePane("main");
      setAdminLitigePaneDossierId(null);
      return;
    }
    if (adminLitigeReturnTo?.type === "transaction") {
      const txId = String(adminLitigeReturnTo.transactionId || "").trim();
      setAdminLitigeDetailId(null);
      setAdminLitigePane("main");
      setAdminLitigePaneDossierId(null);
      setAdminLitigeReturnTo(null);
      clearAdminLitigeFromUrl();
      if (txId) setAdminTransactionDetailId(txId);
    }
  }, [adminLitigePane, adminLitigeReturnTo, clearAdminLitigeFromUrl]);

  const openAdminLitigeModal = useCallback(
    (litigePk, opts) => {
      const id = String(litigePk || "").trim();
      if (!id) return;
      setAdminLitigePane("main");
      setAdminLitigePaneDossierId(null);
      setAdminLitigeDetailId(id);
      if (opts?.returnTo === "transaction" && opts.transactionId) {
        const txId = String(opts.transactionId);
        setAdminLitigeReturnTo({
          type: "transaction",
          transactionId: txId,
        });
        setAdminTransactionDetailId(txId);
      } else {
        setAdminLitigeReturnTo(null);
      }
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.set("litige", id);
            return next;
          },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const openAdminUserModal = useCallback((userId, opts) => {
    const id = String(userId || "").trim();
    if (!id) return;
    setAdminUserPane("main");
    setAdminUserPaneId(null);
    if (opts?.returnTo === "transaction" && opts.transactionId) {
      const txId = String(opts.transactionId);
      setAdminUserReturnTo({
        type: "transaction",
        transactionId: txId,
      });
      setAdminTransactionDetailId(txId);
    } else {
      setAdminUserReturnTo(null);
    }
    setAdminSelectedUserId(id);
  }, []);

  const openAdminUserPeer = useCallback((userId) => {
    const id = String(userId || "").trim();
    if (!id) return;
    setAdminSelectedUserId(id);
    setAdminUserPane("main");
    setAdminUserPaneId(null);
  }, []);

  const openAdminActivityRow = useCallback(
    (a) => {
      if (!a) return;
      if (a.link_dossier_id) {
        setAdminUserPane("dossier");
        setAdminUserPaneId(String(a.link_dossier_id));
      } else if (a.link_litige_id) {
        setAdminUserPane("litige");
        setAdminUserPaneId(String(a.link_litige_id));
      } else if (a.link_profile_id) {
        openAdminUserPeer(a.link_profile_id);
      }
    },
    [openAdminUserPeer],
  );

  const [stats, setStats] = useState(null);
  const [disputes, setDisputes] = useState([]);
  const [recordingLoadTid, setRecordingLoadTid] = useState(null);
  /** Lecture dans la page (évite pop-up bloquée et téléchargement « fantôme » sur blob:). */
  const [callRecordingPlayer, setCallRecordingPlayer] = useState(null);
  const [callRecordingPlayError, setCallRecordingPlayError] = useState("");
  const callRecordingObjectUrlRef = useRef(null);
  const [disputeResolveDraft, setDisputeResolveDraft] = useState(null);
  const [users, setUsers] = useState([]);
  const [adminSelectedUserId, setAdminSelectedUserId] = useState(null);
  const [adminUserDetail, setAdminUserDetail] = useState(null);
  const [adminUserDetailLoading, setAdminUserDetailLoading] = useState(false);
  const [adminUserDetailError, setAdminUserDetailError] = useState("");
  const [adminUserQuery, setAdminUserQuery] = useState("");
  const [produits, setProduits] = useState([]);
  const [secteurs, setSecteurs] = useState([]);
  const [onlineDossiers, setOnlineDossiers] = useState([]);
  const [hiddenDossiers, setHiddenDossiers] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [showProdDialog, setShowProdDialog] = useState(false);
  const [editingProduit, setEditingProduit] = useState(null);
  const [newProd, setNewProd] = useState({ nom: "", description: "", secteur: "", champs: [] });
  const [newChamp, setNewChamp] = useState({
    nom: "",
    type: "text",
    cible_annonces: "tous",
    listOptionRows: [""],
    required: false,
    visible_public: true,
  });
  const [champDragIndex, setChampDragIndex] = useState(null);
  const [champDropOver, setChampDropOver] = useState(null);
  const [showSecDialog, setShowSecDialog] = useState(false);
  const [editingSec, setEditingSec] = useState(null);
  const [newSec, setNewSec] = useState({ nom: "" });
  const [produitSearch, setProduitSearch] = useState("");
  const [produitSecteurFilter, setProduitSecteurFilter] = useState("");
  const [adminPendingQuery, setAdminPendingQuery] = useState("");
  const [adminPendingFilters, setAdminPendingFilters] = useState({
    secteur: "all",
    type: "all",
    produit_id: "all",
    location: "",
  });
  const [adminDisputesQuery, setAdminDisputesQuery] = useState("");
  const [adminSuggestionsQuery, setAdminSuggestionsQuery] = useState("");
  const [adminSecteursQuery, setAdminSecteursQuery] = useState("");
  const [adminTransactions, setAdminTransactions] = useState([]);
  const [adminTransactionsQuery, setAdminTransactionsQuery] = useState("");
  const [adminTxFilter, setAdminTxFilter] = useState("all");
  const [adminTransactionDetailId, setAdminTransactionDetailId] = useState(null);
  const [adminRefreshing, setAdminRefreshing] = useState(false);
  const [overviewDays, setOverviewDays] = useState(30);
  const [overviewStatsLoading, setOverviewStatsLoading] = useState(false);

  const produitsFiltres = useMemo(() => {
    const q = produitSearch.trim().toLowerCase();
    return produits.filter((p) => {
      if (produitSecteurFilter && p.secteur !== produitSecteurFilter) return false;
      if (!q) return true;
      const nom = String(p.nom || "").toLowerCase();
      const desc = String(p.description || "").toLowerCase();
      const champsMatch = Array.isArray(p.champs)
        && p.champs.some((c) => String(c.nom || "").toLowerCase().includes(q));
      return nom.includes(q) || desc.includes(q) || champsMatch;
    });
  }, [produits, produitSearch, produitSecteurFilter]);

  const adminDeletedUserCount = useMemo(
    () => users.filter((u) => isAdminProfileDeleted(u)).length,
    [users],
  );

  const adminUsersChurnedCount = useMemo(
    () => users.filter((u) => isAdminUserSubscriptionChurned(u)).length,
    [users],
  );

  const adminUsersSuspendedCount = useMemo(
    () => users.filter((u) => Boolean(u.suspended_at) && !isAdminProfileDeleted(u)).length,
    [users],
  );

  const adminUsersActive = useMemo(() => {
    switch (adminUsersFilter) {
      case "churned":
        return users.filter((u) => isAdminUserSubscriptionChurned(u));
      case "deleted":
        return users.filter((u) => isAdminProfileDeleted(u));
      case "suspended":
        return users.filter((u) => Boolean(u.suspended_at) && !isAdminProfileDeleted(u));
      default:
        return users.filter((u) => !isAdminProfileDeleted(u));
    }
  }, [users, adminUsersFilter]);

  const adminUsersFiltered = useMemo(() => {
    const q = adminUserQuery.trim().toLowerCase();
    if (!q) return adminUsersActive;
    return adminUsersActive.filter((u) => {
      const name = String(u.name || "").toLowerCase();
      const email = String(u.email || "").toLowerCase();
      const company = String(u.company_name || "").toLowerCase();
      return name.includes(q) || email.includes(q) || company.includes(q);
    });
  }, [adminUsersActive, adminUserQuery]);

  const catalogDossiersAll = useMemo(() => {
    const byId = new Map();
    for (const d of onlineDossiers) {
      byId.set(String(d.dossier_id), { ...d, status: d.status || "online" });
    }
    for (const d of hiddenDossiers) {
      const id = String(d.dossier_id);
      if (!byId.has(id)) byId.set(id, d);
    }
    return [...byId.values()];
  }, [onlineDossiers, hiddenDossiers]);

  const adminPendingSecteurs = useMemo(
    () =>
      [...new Set(catalogDossiersAll.map((d) => d.secteur).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b, "fr"),
      ),
    [catalogDossiersAll],
  );

  const adminPendingProduits = useMemo(() => {
    const secteur =
      adminPendingFilters.secteur && adminPendingFilters.secteur !== "all" ? adminPendingFilters.secteur : null;
    const ids = new Set(
      catalogDossiersAll
        .filter((d) => !secteur || d.secteur === secteur)
        .map((d) => String(d.produit_id || "").trim())
        .filter(Boolean),
    );
    return produits
      .filter((p) => ids.has(String(p.produit_id)))
      .sort((a, b) => String(a.nom || "").localeCompare(String(b.nom || ""), "fr"));
  }, [catalogDossiersAll, produits, adminPendingFilters.secteur]);

  const adminPendingFiltersActive = useMemo(() => {
    const f = adminPendingFilters;
    return (
      (f.secteur && f.secteur !== "all") ||
      (f.type && f.type !== "all") ||
      (f.produit_id && f.produit_id !== "all") ||
      Boolean(String(f.location || "").trim()) ||
      Boolean(String(adminPendingQuery || "").trim())
    );
  }, [adminPendingFilters, adminPendingQuery]);

  const catalogDossiersFiltered = useMemo(() => {
    let rows = catalogDossiersAll;
    const f = adminPendingFilters;
    if (f.secteur && f.secteur !== "all") {
      rows = rows.filter((d) => d.secteur === f.secteur);
    }
    if (f.type && f.type !== "all") {
      rows = rows.filter((d) => normalizeAnnonceType(d.type) === f.type);
    }
    if (f.produit_id && f.produit_id !== "all") {
      rows = rows.filter((d) => String(d.produit_id) === String(f.produit_id));
    }
    const locQ = String(f.location || "").trim().toLowerCase();
    if (locQ) {
      rows = rows.filter((d) => {
        const dept = getDeptDisplayLabel(d);
        const hay = [d.location, dept, d.description].filter(Boolean).join(" ").toLowerCase();
        return hay.includes(locQ);
      });
    }
    const q = adminPendingQuery.trim().toLowerCase();
    if (q) {
      rows = rows.filter((d) => {
        const o = ownerDisplay(d);
        const hay = [
          d.title,
          d.description,
          d.dossier_id,
          d.secteur,
          d.status,
          dossierStatusLabelFr(d.status),
          o.name,
          o.email,
          o.phone,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      });
    }
    const isOnline = (d) => String(d?.status || "").trim().toLowerCase() === "online";
    return [...rows].sort((a, b) => {
      const ao = isOnline(a) ? 0 : 1;
      const bo = isOnline(b) ? 0 : 1;
      if (ao !== bo) return ao - bo;
      return String(a.title || "").localeCompare(String(b.title || ""), "fr");
    });
  }, [catalogDossiersAll, adminPendingQuery, adminPendingFilters]);

  const suggestionsFiltered = useMemo(() => {
    const q = adminSuggestionsQuery.trim().toLowerCase();
    if (!q) return suggestions;
    return suggestions.filter((s) => {
      const hay = [
        s?.message,
        s?.email,
        s?._admin_display_email,
        s?._admin_display_name,
        s?.suggested_products,
        s?.context,
        s?.suggestion_type,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [suggestions, adminSuggestionsQuery]);

  const secteursFiltered = useMemo(() => {
    const q = adminSecteursQuery.trim().toLowerCase();
    if (!q) return secteurs;
    return secteurs.filter((s) => String(s.nom || "").toLowerCase().includes(q));
  }, [secteurs, adminSecteursQuery]);

  const adminSellerQuality = useMemo(() => computeAdminSellerQuality(adminUserDetail), [adminUserDetail]);
  const adminBuyerQuality = useMemo(() => computeAdminBuyerQuality(adminUserDetail), [adminUserDetail]);

  const openDisputesCount = useMemo(
    () =>
      (Array.isArray(disputes) ? disputes : []).filter(
        (d) => d?.status === "open" || d?.status === "in_review"
      ).length,
    [disputes]
  );

  const adminTxAttentionCount = useMemo(
    () => (Array.isArray(adminTransactions) ? adminTransactions : []).filter((t) => t.needs_attention).length,
    [adminTransactions],
  );

  const adminOverviewSnapshot = useMemo(() => {
    const txs = Array.isArray(adminTransactions) ? adminTransactions : [];
    const st = (t) => String(t?.statut || "").toLowerCase();
    return {
      disputesOpen: openDisputesCount,
      txAttention: adminTxAttentionCount,
      payoutDue: txs.filter((t) => t.payout_due).length,
      sequestre: txs.filter((t) => ["paye", "appel_en_cours", "appel_effectue", "litige"].includes(st(t))).length,
      pendingRdv: txs.filter((t) => st(t) === "appel_effectue").length,
    };
  }, [adminTransactions, openDisputesCount, adminTxAttentionCount]);

  const handleOverviewOpenTransactions = useCallback(
    ({ filter = "all", detailId } = {}) => {
      setAdminTxFilter(filter === "attention" ? "attention" : "all");
      setActiveTab("transactions");
      if (detailId) setAdminTransactionDetailId(String(detailId));
    },
    [setActiveTab],
  );

  const handleOverviewOpenDossier = useCallback(
    (dossierId) => {
      setActiveTab("pending");
      setAdminPendingDetailId(String(dossierId));
    },
    [setActiveTab],
  );

  const adminNavItems = useMemo(
    () => [
      {
        value: "overview",
        label: "Vue d'ensemble",
        icon: ADMIN_TAB_ICONS.overview,
        testId: "admin-tab-overview",
        count: 0,
        showBadge: false,
      },
      {
        value: "pending",
        label: "Modération",
        icon: ADMIN_TAB_ICONS.pending,
        testId: "admin-tab-pending",
        count: 0,
        showBadge: false,
      },
      {
        value: "diffusion",
        label: "Diffusion",
        icon: ADMIN_TAB_ICONS.diffusion,
        testId: "admin-tab-diffusion",
        count: 0,
        showBadge: false,
      },
      {
        value: "disputes",
        label: "Litiges",
        icon: ADMIN_TAB_ICONS.disputes,
        testId: "admin-tab-disputes",
        count: openDisputesCount,
        badgeVariant: openDisputesCount > 0 ? "alert" : undefined,
      },
      {
        value: "transactions",
        label: "Transactions",
        icon: ADMIN_TAB_ICONS.transactions,
        testId: "admin-tab-transactions",
        count: adminTxAttentionCount,
        badgeVariant: adminTxAttentionCount > 0 ? "alert" : undefined,
        showBadge: adminTxAttentionCount > 0,
      },
      {
        value: "contact",
        label: "Contact",
        icon: ADMIN_TAB_ICONS.contact,
        testId: "admin-tab-contact",
        count: suggestions.length,
      },
      {
        value: "users",
        label: "Utilisateurs",
        icon: ADMIN_TAB_ICONS.users,
        testId: "admin-tab-users",
        count: users.length,
        showBadge: false,
      },
      {
        value: "produits",
        label: "Produits",
        icon: ADMIN_TAB_ICONS.produits,
        testId: "admin-tab-produits",
        count: produits.length,
        showBadge: false,
      },
      {
        value: "secteurs",
        label: "Secteurs",
        icon: ADMIN_TAB_ICONS.secteurs,
        testId: "admin-tab-secteurs",
        count: secteurs.length,
        showBadge: false,
      },
    ],
    [openDisputesCount, adminTxAttentionCount, suggestions.length, users.length, produits.length, secteurs.length]
  );

  const adminPageMeta = ADMIN_PAGE_META[activeTab] || ADMIN_PAGE_META.overview;

  const disputesSorted = useMemo(() => {
    const q = adminDisputesQuery.trim().toLowerCase();
    let list = Array.isArray(disputes) ? [...disputes] : [];
    if (q) {
      list = list.filter((d) => {
        const hay = [
          d.dossier?.title,
          d.motif,
          d.reason,
          d.acheteur_name,
          d.acheteur_email,
          d.vendeur_name,
          d.vendeur_email,
          d.message_partie,
          d.description,
          d.id,
          d.dispute_id,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      });
    }
    const h = SELLER_PAYOUT_HOURS;
    return list.sort((a, b) => {
      const oa = isLitigeOpenRow(a);
      const ob = isLitigeOpenRow(b);
      if (oa !== ob) return oa ? -1 : 1;
      if (oa) {
        const ea = disputeWindowEndMs(a, h);
        const eb = disputeWindowEndMs(b, h);
        if (ea !== eb) return ea - eb;
        const ca = Date.parse(String(a.created_at || "")) || 0;
        const cb = Date.parse(String(b.created_at || "")) || 0;
        return ca - cb;
      }
      const ra = Date.parse(String(a.resolved_at || a.updated_at || a.created_at || "")) || 0;
      const rb = Date.parse(String(b.resolved_at || b.updated_at || b.created_at || "")) || 0;
      return rb - ra;
    });
  }, [disputes, adminDisputesQuery]);

  const adminLitigeDetailRow = useMemo(() => {
    if (!adminLitigeDetailId) return null;
    return disputesSorted.find((x) => String(x.id || x.dispute_id) === adminLitigeDetailId) ?? null;
  }, [adminLitigeDetailId, disputesSorted]);

  const adminUserLitigeRow = useMemo(() => {
    if (adminUserPane !== "litige" || !adminUserPaneId) return null;
    return disputesSorted.find((x) => String(x.id || x.dispute_id) === adminUserPaneId) ?? null;
  }, [adminUserPane, adminUserPaneId, disputesSorted]);

  const adminPendingDetailRow = useMemo(() => {
    if (!adminPendingDetailId) return null;
    return (
      catalogDossiersFiltered.find((d) => String(d.dossier_id) === adminPendingDetailId) ??
      catalogDossiersAll.find((d) => String(d.dossier_id) === adminPendingDetailId) ??
      null
    );
  }, [adminPendingDetailId, catalogDossiersFiltered, catalogDossiersAll]);

  const adminSuggestionDetailRow = useMemo(() => {
    if (!adminSuggestionDetailId) return null;
    return suggestionsFiltered.find((s) => String(s.id) === adminSuggestionDetailId) ?? null;
  }, [adminSuggestionDetailId, suggestionsFiltered]);

  const adminProduitDetailRow = useMemo(() => {
    if (!adminProduitDetailId) return null;
    return produitsFiltres.find((p) => String(p.produit_id) === adminProduitDetailId) ?? null;
  }, [adminProduitDetailId, produitsFiltres]);

  const adminSecteurDetailRow = useMemo(() => {
    if (!adminSecteurDetailId) return null;
    return secteursFiltered.find((s) => String(s.secteur_id) === adminSecteurDetailId) ?? null;
  }, [adminSecteurDetailId, secteursFiltered]);

  const litigeDeepLinkToastRef = useRef("");

  useEffect(() => {
    if (activeTab !== "disputes" || !focusedLitigeId) {
      litigeDeepLinkToastRef.current = "";
      return undefined;
    }
    const rowKey = focusedLitigeId;
    if (disputesSorted.length === 0) return undefined;
    const match = disputesSorted.some((d) => String(d.id || d.dispute_id) === rowKey);
    if (!match) {
      if (litigeDeepLinkToastRef.current !== `nf:${rowKey}`) {
        litigeDeepLinkToastRef.current = `nf:${rowKey}`;
        toast.info(
          "Ce litige n’apparaît pas dans la liste chargée. Rechargez la page admin ou vérifiez l’identifiant.",
          { duration: 6000 },
        );
      }
      return undefined;
    }
    litigeDeepLinkToastRef.current = `ok:${rowKey}`;
    setAdminLitigeReturnTo(null);
    setAdminLitigeDetailId(rowKey);
    const t = window.setTimeout(() => {
      document.querySelector(`[data-testid="admin-dispute-row-${rowKey}"]`)?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }, 120);
    return () => window.clearTimeout(t);
  }, [activeTab, focusedLitigeId, disputesSorted]);

  /** Rafraîchit l’affichage « temps restant » sur les lignes litiges (ouverts). */
  const [, setLitigeTempsTick] = useState(0);
  useEffect(() => {
    if (!Array.isArray(disputes) || disputes.length === 0) return undefined;
    const hasOpen = disputes.some((d) => isLitigeOpenRow(d));
    if (!hasOpen) return undefined;
    const id = window.setInterval(() => {
      setLitigeTempsTick((n) => n + 1);
    }, 30000);
    return () => window.clearInterval(id);
  }, [disputes]);

  useEffect(() => {
    return () => {
      if (callRecordingObjectUrlRef.current) {
        URL.revokeObjectURL(callRecordingObjectUrlRef.current);
        callRecordingObjectUrlRef.current = null;
      }
    };
  }, []);

  const loadSuggestions = useCallback(async () => {
    setSuggestionsLoading(true);
    try {
      const { data, error } = await supabase
        .from("product_suggestions")
        .select("id, created_at, context, user_id, email, suggestion_type, suggested_products, message")
        .order("created_at", { ascending: false })
        .limit(200);
      if (error) throw error;
      const rows = Array.isArray(data) ? data : [];
      const uids = [...new Set(rows.map((r) => r.user_id).filter(Boolean))];
      const profBy = {};
      if (uids.length) {
        const { data: profs, error: pe } = await supabase
          .from("profiles")
          .select("id, first_name, last_name, name, nom, email")
          .in("id", uids);
        if (pe) console.warn("admin suggestions profiles:", pe.message);
        (profs || []).forEach((p) => {
          if (p?.id) profBy[p.id] = p;
        });
      }
      setSuggestions(
        rows.map((r) => {
          const prof = r.user_id ? profBy[r.user_id] : null;
          const fromProf = profileDisplayName(prof);
          const resolvedEmail = String((prof && prof.email) || r.email || "").trim();
          return {
            ...r,
            _admin_display_name: fromProf || null,
            _admin_display_email: resolvedEmail || null,
          };
        }),
      );
    } catch (e) {
      setSuggestions([]);
      toast.error(e?.message || "Impossible de charger les messages de contact");
    } finally {
      setSuggestionsLoading(false);
    }
  }, []);

  const revokeCallRecordingObjectUrl = useCallback(() => {
    if (callRecordingObjectUrlRef.current) {
      URL.revokeObjectURL(callRecordingObjectUrlRef.current);
      callRecordingObjectUrlRef.current = null;
    }
  }, []);

  const closeCallRecordingPlayer = useCallback(() => {
    revokeCallRecordingObjectUrl();
    setCallRecordingPlayer(null);
    setCallRecordingPlayError("");
  }, [revokeCallRecordingObjectUrl]);

  const handleAdminListenRecording = useCallback(async (transactionId, opts) => {
    const tid = String(transactionId || "").trim();
    const callSessionId = String(opts?.callSessionId || "").trim();
    if (!tid && !callSessionId) return;
    const loadKey = callSessionId ? `session:${callSessionId}` : tid;
    setRecordingLoadTid(loadKey);
    revokeCallRecordingObjectUrl();
    setCallRecordingPlayer(null);
    setCallRecordingPlayError("");
    try {
      const { data: blob, headers } = await fetchAdminCallRecording(tid, { callSessionId });
      const ab = await blob.arrayBuffer();
      const bytes = new Uint8Array(ab);
      const sniffMime = () => {
        if (bytes.length >= 3 && bytes[0] === 0xff && (bytes[1] & 0xe0) === 0xe0) return "audio/mpeg";
        if (
          bytes.length >= 12 &&
          bytes[0] === 0x52 &&
          bytes[1] === 0x49 &&
          bytes[2] === 0x46 &&
          bytes[3] === 0x46
        ) {
          return "audio/wav";
        }
        if (bytes.length >= 4 && bytes[0] === 0x4f && bytes[1] === 0x67 && bytes[2] === 0x67 && bytes[3] === 0x53) {
          return "audio/ogg";
        }
        return null;
      };
      const hdr = String(headers?.get?.("content-type") || "")
        .split(";")[0]
        .trim()
        .toLowerCase();
      const blobDeclared = String(blob.type || "")
        .split(";")[0]
        .trim()
        .toLowerCase();
      let mime = sniffMime() || "audio/mpeg";
      if (hdr.startsWith("audio/")) mime = hdr;
      else if (blobDeclared.startsWith("audio/")) mime = blobDeclared;
      if (!ab.byteLength) {
        throw new Error("Fichier audio vide (0 octet).");
      }
      const typedBlob = new Blob([ab], { type: mime });
      const u = URL.createObjectURL(typedBlob);
      callRecordingObjectUrlRef.current = u;
      setCallRecordingPlayer({ url: u, transactionId: tid, mime, size: ab.byteLength });
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : e?.message || "Enregistrement indisponible");
    } finally {
      setRecordingLoadTid(null);
    }
  }, [revokeCallRecordingObjectUrl]);

  const fetchAdminStats = useCallback(async (days) => {
    const d = [1, 7, 30, 90, 180, 365].includes(Number(days)) ? Number(days) : 30;
    setOverviewStatsLoading(true);
    try {
      const r = await api.get("/admin/stats", { params: { days: d } });
      setStats(r.data && typeof r.data === "object" && !Array.isArray(r.data) ? r.data : null);
    } catch {
      setStats(null);
    } finally {
      setOverviewStatsLoading(false);
    }
  }, []);

  const load = useCallback(async () => {
    setAdminRefreshing(true);
    try {
    const errDetail = (e) => e?.response?.data?.detail || e?.message || "Erreur inconnue";
    const blockNames = [
      { key: "stats", label: "Statistiques (API /admin/stats)" },
      { key: "online", label: "Annonces en ligne" },
      { key: "hidden", label: "Annonces masquées" },
      { key: "disputes", label: "Litiges" },
      { key: "transactions", label: "Transactions" },
      { key: "users", label: "Utilisateurs" },
      { key: "produits", label: "Produits" },
      { key: "secteurs", label: "Secteurs" },
    ];
    const settled = await Promise.allSettled([
      api.get("/admin/stats", { params: { days: overviewDays } }),
      api.get("/admin/dossiers/online"),
      api.get("/admin/dossiers/hidden"),
      api.get("/admin/disputes"),
      api.get("/admin/transactions"),
      api.get("/admin/users"),
      api.get("/produits"),
      api.get("/secteurs"),
    ]);
    const failures = [];
    settled.forEach((r, i) => {
      if (r.status === "rejected") failures.push({ ...blockNames[i], err: r.reason });
    });
    if (settled[0].status === "fulfilled") {
      const s = settled[0].value;
      setStats(s.data && typeof s.data === "object" && !Array.isArray(s.data) ? s.data : null);
    } else {
      setStats(null);
    }
    if (settled[1].status === "fulfilled") setOnlineDossiers(asList(settled[1].value.data));
    else setOnlineDossiers([]);
    if (settled[2].status === "fulfilled") setHiddenDossiers(asList(settled[2].value.data));
    else setHiddenDossiers([]);
    if (settled[3].status === "fulfilled") setDisputes(asList(settled[3].value.data));
    else setDisputes([]);
    if (settled[4].status === "fulfilled") setAdminTransactions(asList(settled[4].value.data));
    else setAdminTransactions([]);
    if (settled[5].status === "fulfilled") setUsers(asList(settled[5].value.data));
    else setUsers([]);
    if (settled[6].status === "fulfilled") setProduits(asList(settled[6].value.data));
    else setProduits([]);
    if (settled[7].status === "fulfilled") setSecteurs(asList(settled[7].value.data));
    else setSecteurs([]);
    if (failures.length === 0) return;
    if (failures.length === settled.length) {
      toast.error(`Aucun bloc n’a pu être chargé. ${errDetail(failures[0].err)}`, { duration: 8000 });
    } else {
      toast.warning(
        `Chargement partiel : ${failures.map((f) => f.key).join(", ")}. ${errDetail(failures[0].err)}`,
        { duration: 10000 }
      );
    }
    } finally {
      setAdminRefreshing(false);
    }
  }, [overviewDays]);

  const loadAdminUserDetail = useCallback(async (uid) => {
    if (!uid) return;
    setAdminUserDetailLoading(true);
    setAdminUserDetailError("");
    try {
      const r = await api.get("/admin/user-detail", { params: { user_id: uid } });
      setAdminUserDetail(r.data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setAdminUserDetailError(typeof d === "string" ? d : e?.message || "Erreur");
      setAdminUserDetail(null);
    } finally {
      setAdminUserDetailLoading(false);
    }
  }, []);

  const adminUserAccountAction = async (userId, action) => {
    const sum = adminUserDetail?.summary;
    const name = sum?.name || sum?.email || "cet utilisateur";
    if (action === "delete") {
      const ok = window.confirm(
        `Supprimer définitivement le compte « ${name} » ?\n\n` +
          "Les annonces en ligne seront retirées. La connexion sera désactivée. Cette action est irréversible.",
      );
      if (!ok) return;
    } else if (action === "suspend") {
      const ok = window.confirm(
        `Suspendre « ${name} » ?\n\nL’utilisateur ne pourra plus se connecter. Ses annonces en ligne seront retirées.`,
      );
      if (!ok) return;
    }

    try {
      const res = await api.post("/admin/user-account", { user_id: userId, action });
      const payload = res?.data && typeof res.data === "object" ? res.data : {};
      if (action === "delete") {
        setAdminSelectedUserId(null);
        setAdminUserDetail(null);
        if (payload.warning) toast.warning(payload.warning);
        else if (payload.auth_removed === false && payload.auth_banned) {
          toast.success("Compte désactivé (connexion bloquée).");
        } else toast.success("Compte supprimé");
      } else if (action === "suspend") {
        toast.success("Compte suspendu");
      } else {
        toast.success("Compte réactivé");
      }
      await load();
      if (action !== "delete" && adminSelectedUserId === userId) {
        await loadAdminUserDetail(userId);
      }
    } catch (e) {
      const detail = String(e?.response?.data?.detail || e?.message || "");
      if (action === "delete" && detail.includes("Profil marqué supprimé")) {
        toast.warning(detail);
        setAdminSelectedUserId(null);
        setAdminUserDetail(null);
        await load();
        return;
      }
      toast.error(detail || "Action impossible");
    }
  };

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!adminSelectedUserId) {
      setAdminUserDetail(null);
      setAdminUserDetailError("");
      return;
    }
    void loadAdminUserDetail(adminSelectedUserId);
  }, [adminSelectedUserId, loadAdminUserDetail]);

  useEffect(() => {
    if (activeTab !== "users" || !adminUserFromUrl) return;
    if (adminSelectedUserId === adminUserFromUrl) return;
    setAdminSelectedUserId(adminUserFromUrl);
    setAdminUserPane("main");
    setAdminUserPaneId(null);
  }, [activeTab, adminUserFromUrl, adminSelectedUserId]);

  useEffect(() => {
    if (searchParams.get("tab") !== "suggestions") return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", "contact");
        return next;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (activeTab === "contact") void loadSuggestions();
  }, [activeTab, loadSuggestions]);

  useEffect(() => {
    if (!showProdDialog) {
      setChampDragIndex(null);
      setChampDropOver(null);
    }
  }, [showProdDialog]);

  const confirmResolveDispute = async () => {
    if (!disputeResolveDraft?.id || !disputeResolveDraft?.decision) return;
    try {
      await api.post(
        `/admin/disputes/${disputeResolveDraft.id}/resolve`,
        { resolution_message: String(disputeResolveDraft.message || "").trim() },
        { params: { decision: disputeResolveDraft.decision } },
      );
      toast.success("Litige résolu");
      setDisputeResolveDraft(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const openAdminEditDossier = async (dossierId) => {
    const id = String(dossierId || "").trim();
    if (!id) return;
    try {
      const { data } = await api.get(`/dossiers/${id}`);
      setAdminEditDossier(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Impossible de charger l’annonce");
    }
  };

  const hideDossierFromMarketplace = async (dossierId) => {
    if (
      !window.confirm(
        "Masquer cette annonce de la marketplace ? Elle ne sera plus visible dans le catalogue public.",
      )
    ) {
      return;
    }
    try {
      await api.put(`/admin/dossiers/${dossierId}/withdraw`, null);
      toast.success("Annonce masquée de la marketplace");
      setAdminPendingDetailId((cur) =>
        cur && String(cur) === String(dossierId) ? null : cur,
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const deleteDossierPermanently = async (dossierId) => {
    if (
      !window.confirm(
        "Supprimer définitivement cette annonce ?\n\nSi elle est liée à des achats ou négociations, elle sera seulement retirée du catalogue (historique conservé). Sinon elle sera effacée de la base.",
      )
    ) {
      return;
    }
    try {
      const res = await api.delete(`/admin/dossiers/${dossierId}/delete`);
      const removed = res?.data?.removed;
      const detail = res?.data?.detail;
      if (removed === "retired") {
        toast.warning(detail || "Annonce retirée du catalogue (historique conservé).");
      } else {
        toast.success(detail || "Annonce supprimée.");
      }
      setAdminPendingDetailId((cur) => (cur && String(cur) === String(dossierId) ? null : cur));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Suppression impossible");
    }
  };

  const publishDossierToMarketplace = async (dossierId) => {
    if (
      !window.confirm(
        "Remettre cette annonce en ligne sur la marketplace ? Elle sera à nouveau visible dans le catalogue.",
      )
    ) {
      return;
    }
    try {
      await api.put(`/admin/dossiers/${dossierId}/publish`, null);
      toast.success("Annonce remise en ligne");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const addChamp = () => {
    if (!newChamp.nom) return;
    if (newChamp.type === "list") {
      const opts = sanitizeListOptionsForSave(newChamp.listOptionRows || []);
      if (opts.length === 0) return toast.error("Menu déroulant : ajoutez au moins une option (champ rempli).");
    }
    setNewProd({
      ...newProd,
      champs: [
        ...newProd.champs,
        normalizeChampForForm({
          nom: newChamp.nom,
          type: newChamp.type,
          cible_annonces: newChamp.cible_annonces === "vente" || newChamp.cible_annonces === "recherche" ? newChamp.cible_annonces : "tous",
          required: Boolean(newChamp.required),
          visible_public: newChamp.visible_public !== false,
          options: newChamp.type === "list" ? sanitizeListOptionsForSave(newChamp.listOptionRows || []) : [],
        }),
      ],
    });
    setNewChamp({ nom: "", type: "text", cible_annonces: "tous", listOptionRows: [""], required: false, visible_public: true });
  };

  const setChampAt = (i, partial) => {
    setNewProd((prev) => {
      const ch = [...prev.champs];
      const next = { ...ch[i], ...partial };
      if (partial.type != null && partial.type !== "list") {
        next.options = [];
      }
      if (partial.type === "list") {
        const prevOpts = ch[i].options;
        next.options =
          Array.isArray(prevOpts) && prevOpts.length > 0 ? prevOpts.map(String) : [""];
      }
      ch[i] = next;
      return { ...prev, champs: ch };
    });
  };

  const setListOptionCell = (champIndex, optionIndex, value) => {
    setNewProd((prev) => {
      const ch = [...prev.champs];
      const cur = { ...ch[champIndex] };
      const rows = listOptionsToRows(cur.options);
      rows[optionIndex] = value;
      cur.options = rows;
      ch[champIndex] = cur;
      return { ...prev, champs: ch };
    });
  };

  const addListOptionCell = (champIndex) => {
    setNewProd((prev) => {
      const ch = [...prev.champs];
      const cur = { ...ch[champIndex] };
      const rows = listOptionsToRows(cur.options);
      rows.push("");
      cur.options = rows;
      ch[champIndex] = cur;
      return { ...prev, champs: ch };
    });
  };

  const removeListOptionCell = (champIndex, optionIndex) => {
    setNewProd((prev) => {
      const ch = [...prev.champs];
      const cur = { ...ch[champIndex] };
      let rows = listOptionsToRows(cur.options);
      rows = rows.filter((_, k) => k !== optionIndex);
      if (rows.length === 0) rows = [""];
      cur.options = rows;
      ch[champIndex] = cur;
      return { ...prev, champs: ch };
    });
  };

  const onChampDragStart = (e, i) => {
    e.dataTransfer.setData("text/x-rdv-champ-idx", String(i));
    e.dataTransfer.effectAllowed = "move";
    setChampDragIndex(i);
    setChampDropOver(null);
  };

  const onChampDragEnd = () => {
    setChampDragIndex(null);
    setChampDropOver(null);
  };

  const onChampDragOver = (e, i) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setChampDropOver((p) => (p === i ? p : i));
  };

  const onChampDrop = (e, to) => {
    e.preventDefault();
    const raw = e.dataTransfer.getData("text/x-rdv-champ-idx");
    const from = parseInt(raw, 10);
    if (raw === "" || Number.isNaN(from) || from === to) {
      onChampDragEnd();
      return;
    }
    setNewProd((prev) => ({ ...prev, champs: moveChampInList(prev.champs, from, to) }));
    onChampDragEnd();
  };

  const champsForProduitClone = (p) =>
    (p.champs || []).map((c) => {
      const { champ_id, id, ...rest } = c;
      return normalizeChampForForm({
        ...rest,
        options: Array.isArray(c.options) ? [...c.options] : [],
      });
    });

  const duplicateProduit = async (p) => {
    const base = String(p.nom || "").trim() || "Produit";
    const payload = {
      nom: `${base} (copie)`,
      description: p.description || "",
      secteur: p.secteur,
      champs: champsForProduitClone(p).map((c) =>
        c.type === "list"
          ? { ...c, options: sanitizeListOptionsForSave(listOptionsToRows(c.options)) }
          : c
      ),
    };
    if (!payload.secteur) {
      toast.error("Secteur manquant sur le produit source : éditez le produit d’abord.");
      return;
    }
    try {
      await api.post("/produits", payload);
      toast.success("Produit dupliqué. Renommez-le si besoin (Éditer).");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Erreur à la duplication");
    }
  };

  const createProd = async () => {
    if (!newProd.nom || !newProd.secteur) return toast.error("Nom et secteur requis");
    const nomsVides = newProd.champs?.some((c) => c && !String(c.nom ?? "").trim());
    if (nomsVides) return toast.error("Chaque champ dynamique doit avoir un libellé, ou supprimez la ligne vide.");
    const listSansOption = newProd.champs?.some(
      (c) => c?.type === "list" && sanitizeListOptionsForSave(listOptionsToRows(c.options)).length === 0
    );
    if (listSansOption) {
      return toast.error("Chaque menu déroulant doit avoir au moins une option remplie.");
    }
    if (texteContientCoordonnees(newProd.description)) {
      return toast.error(messageCoordonneesInterdites);
    }
    const champsPourApi = (newProd.champs || []).map((c) =>
      c.type === "list"
        ? { ...c, options: sanitizeListOptionsForSave(listOptionsToRows(c.options)) }
        : c
    );
    const payload = {
      nom: newProd.nom,
      description: newProd.description,
      secteur: newProd.secteur,
      champs: champsPourApi,
    };
    const produitId = editingProduit?.produit_id ?? editingProduit?.id;
    try {
      if (editingProduit) {
        if (!produitId) {
          toast.error("Identifiant produit manquant — rechargez la page.");
          return;
        }
        const { data: saved } = await api.put(`/produits/${produitId}`, payload);
        setProduits((prev) =>
          prev.map((p) => (String(p.produit_id) === String(produitId) ? { ...p, ...saved } : p)),
        );
        toast.success("Produit mis à jour");
      } else {
        const { data: saved } = await api.post("/produits", payload);
        setProduits((prev) => [...prev, saved]);
        toast.success("Produit créé");
      }
      setShowProdDialog(false);
      setEditingProduit(null);
      setNewProd({ nom: "", description: "", secteur: "", champs: [] });
      setNewChamp({ nom: "", type: "text", cible_annonces: "tous", listOptionRows: [""], required: false, visible_public: true });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Erreur à l’enregistrement");
    }
  };

  const openEditProduit = (p) => {
    setEditingProduit(p);
    setNewProd({
      nom: p.nom,
      description: p.description || "",
      secteur: p.secteur,
      champs: (p.champs || []).map(normalizeChampForForm),
    });
    setShowProdDialog(true);
  };

  const deleteProd = async (id) => {
    if (!window.confirm("Désactiver ce produit ?")) return;
    try {
      await api.delete(`/produits/${id}`);
      toast.success("Produit désactivé");
      load();
    } catch { toast.error("Erreur"); }
  };

  const createSec = async () => {
    if (!newSec.nom) return toast.error("Nom requis");
    const secPayload = {
      nom: newSec.nom.trim(),
      description: "",
      color: SECTEUR_COULEUR_DEFAUT,
    };
    try {
      if (editingSec) {
        await api.put(`/secteurs/${editingSec.secteur_id}`, secPayload);
        toast.success("Secteur mis à jour");
      } else {
        await api.post("/secteurs", secPayload);
        toast.success("Secteur créé");
      }
      setShowSecDialog(false);
      setEditingSec(null);
      setNewSec({ nom: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const openEditSec = (s) => {
    setEditingSec(s);
    setNewSec({ nom: s.nom });
    setShowSecDialog(true);
  };

  const deleteSec = async (id) => {
    if (!window.confirm("Désactiver ce secteur ?")) return;
    try {
      await api.delete(`/secteurs/${id}`);
      toast.success("Secteur désactivé");
      load();
    } catch { toast.error("Erreur"); }
  };

  return (
    <>
      <AdminShell
        activeTab={activeTab}
        onNavigate={setActiveTab}
        navItems={adminNavItems}
        title={adminPageMeta.title}
        subtitle={adminPageMeta.subtitle}
        kpis={null}
        onRefresh={() => void load()}
        refreshing={adminRefreshing}
        lockMainScroll={activeTab === "pending" && Boolean(adminPendingDetailId)}
      >

        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className={cn(
            "space-y-0",
            activeTab === "pending" && adminPendingDetailId && "flex min-h-0 flex-1 flex-col overflow-hidden",
          )}
        >
          <TabsContent value="overview" className="mt-0 space-y-6 focus-visible:outline-none" data-testid="admin-overview">
            <AdminOverviewDashboard
              stats={stats}
              overviewDays={overviewDays}
              onOverviewDaysChange={(d) => {
                setOverviewDays(d);
                void fetchAdminStats(d);
              }}
              loading={overviewStatsLoading || (adminRefreshing && !stats)}
              snapshot={adminOverviewSnapshot}
              onNavigateTab={setActiveTab}
              onOpenTransactions={handleOverviewOpenTransactions}
              onOpenDispute={openAdminLitigeModal}
              onOpenUser={openAdminUserModal}
              onOpenDossier={handleOverviewOpenDossier}
            />
          </TabsContent>

          <TabsContent
            value="pending"
            className={cn(
              "mt-0 focus-visible:outline-none",
              adminPendingDetailId
                ? "flex min-h-0 flex-1 flex-col gap-4 overflow-y-hidden"
                : "space-y-4",
            )}
          >
            <p className="shrink-0 text-sm text-slate-600">
              Catalogue marketplace : annonces <strong>en ligne</strong> et <strong>masquées / archivées</strong> dans
              la même liste. Cliquez une ligne pour ouvrir la fiche — <strong>Masquer</strong>,{" "}
              <strong>Remettre en ligne</strong> ou <strong>Supprimer</strong> selon le statut.
            </p>
            <div
              className="shrink-0 rounded-xl border border-slate-200/90 bg-white p-3 shadow-sm"
              data-testid="admin-pending-filters"
            >
              <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <SlidersHorizontal className="h-3.5 w-3.5 text-[#2D7FF9]" aria-hidden />
                Filtres
                </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
                <Select
                  value={adminPendingFilters.secteur}
                  onValueChange={(v) =>
                    setAdminPendingFilters((prev) => ({
                      ...prev,
                      secteur: v,
                      produit_id: "all",
                    }))
                  }
                >
                  <SelectTrigger className="h-10 bg-white text-sm" data-testid="admin-pending-filter-secteur">
                    <SelectValue placeholder="Secteur" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les secteurs</SelectItem>
                    {adminPendingSecteurs.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={adminPendingFilters.type}
                  onValueChange={(v) => setAdminPendingFilters((prev) => ({ ...prev, type: v }))}
                >
                  <SelectTrigger className="h-10 bg-white text-sm" data-testid="admin-pending-filter-type">
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les types</SelectItem>
                    <SelectItem value="vente">Offres (vente)</SelectItem>
                    <SelectItem value="recherche">Recherches</SelectItem>
                  </SelectContent>
                </Select>
                <Select
                  value={adminPendingFilters.produit_id}
                  onValueChange={(v) => setAdminPendingFilters((prev) => ({ ...prev, produit_id: v }))}
                >
                  <SelectTrigger className="h-10 bg-white text-sm" data-testid="admin-pending-filter-produit">
                    <SelectValue placeholder="Produit" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les produits</SelectItem>
                    {adminPendingProduits.map((p) => (
                      <SelectItem key={p.produit_id} value={String(p.produit_id)}>
                        {p.nom}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  placeholder="Département / localisation"
                  value={adminPendingFilters.location}
                  onChange={(e) =>
                    setAdminPendingFilters((prev) => ({ ...prev, location: e.target.value }))
                  }
                  className="h-10 bg-white text-sm"
                  data-testid="admin-pending-filter-location"
                  aria-label="Filtrer par département ou localisation"
                />
                </div>
              {adminPendingFiltersActive ? (
                <div className="mt-2 flex justify-end">
                <Button
                  type="button"
                    variant="ghost"
                  size="sm"
                    className="h-8 gap-1 text-slate-600"
                  onClick={() => {
                      setAdminPendingQuery("");
                      setAdminPendingFilters({
                        secteur: "all",
                        type: "all",
                        produit_id: "all",
                        location: "",
                      });
                    }}
                    data-testid="admin-pending-filters-clear"
                  >
                    <X className="h-3.5 w-3.5" aria-hidden />
                    Réinitialiser les filtres
                </Button>
              </div>
            ) : null}
            </div>
            <div
              className={cn(
                adminPendingDetailId
                  ? "flex min-h-0 flex-1 flex-col gap-4 overflow-y-hidden lg:grid lg:grid-cols-[minmax(280px,0.95fr)_minmax(320px,1.05fr)] lg:items-stretch lg:gap-5"
                  : "grid gap-4",
              )}
              >
                <div
              className={
                adminPendingDetailId ? "flex min-h-0 flex-1 flex-col overflow-hidden lg:min-h-0" : undefined
              }
            >
            <AdminListShell
              scrollableList={Boolean(adminPendingDetailId)}
              className={adminPendingDetailId ? "h-full min-h-0" : undefined}
              searchId="admin-pending-search"
              searchPlaceholder="Titre, description, propriétaire…"
              searchValue={adminPendingQuery}
              onSearchChange={(e) => setAdminPendingQuery(e.target.value)}
              count={catalogDossiersAll.length === 0 ? 0 : catalogDossiersFiltered.length}
              total={catalogDossiersAll.length}
              countNoun="annonce"
              emptyMessage={
                catalogDossiersAll.length === 0
                  ? "Aucune annonce pour le moment."
                  : "Aucune annonce ne correspond aux filtres."
              }
              listTestId="admin-pending-list"
              columnGridClassName={
                adminPendingDetailId
                  ? "sm:grid-cols-[minmax(0,1fr)_auto]"
                  : "sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]"
              }
              columns={
                adminPendingDetailId
                  ? [
                      { key: "titre", label: "Annonce", className: "sm:col-start-1" },
                      { key: "action", label: "", className: "sm:col-start-2 text-right" },
                    ]
                  : [
                      { key: "titre", label: "Annonce", className: "sm:col-start-1" },
                      { key: "owner", label: "Propriétaire", className: "sm:col-start-2" },
                      { key: "action", label: "", className: "sm:col-start-3 text-right" },
                    ]
              }
            >
              {catalogDossiersFiltered.map((d) => {
                const rowKey = String(d.dossier_id);
                const o = ownerDisplay(d);
                const st = String(d.status || "").trim().toLowerCase();
                const isOnline = st === "online";
                return (
                  <li key={rowKey} className="min-w-0 border-b border-slate-100 last:border-0">
                    <div
                      className={cn(
                        "flex w-full min-w-0 items-stretch",
                        adminPendingDetailId === rowKey
                          ? "bg-sky-50/80 shadow-[inset_3px_0_0_0_#2D7FF9]"
                          : undefined,
                      )}
                    >
                      <button
                        type="button"
                        onClick={() => setAdminPendingDetailId(rowKey)}
                        className={cn(
                          "flex min-w-0 flex-1 items-center gap-2 px-4 py-3 text-left transition-colors hover:bg-sky-50/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/25 sm:py-2.5",
                          adminPendingDetailId
                            ? "flex-col items-start gap-0.5 sm:flex-col sm:items-start"
                            : "sm:grid sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] sm:items-center sm:gap-2 sm:px-4",
                        )}
                        data-testid={`admin-catalog-row-${rowKey}`}
                      >
                        <div className="min-w-0 w-full flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="truncate text-sm font-semibold text-slate-900">{d.title}</span>
                            {!isOnline ? (
                              <Badge variant="outline" className="shrink-0 text-[10px] font-normal">
                                {dossierStatusLabelFr(st)}
                              </Badge>
                            ) : null}
                          </div>
                          <div
                            className={cn(
                              "line-clamp-1 text-[11px] text-slate-500",
                              adminPendingDetailId ? "mt-0.5" : "sm:hidden",
                            )}
                          >
                            {adminOwnerLine(o)}
                          </div>
                        </div>
                        {!adminPendingDetailId ? (
                          <div className="hidden min-w-0 truncate text-xs text-slate-600 sm:block">
                            {adminOwnerLine(o)}
                          </div>
                        ) : null}
                      </button>
                      <div className="flex shrink-0 flex-wrap items-center justify-end gap-1 border-l border-slate-100 px-2 py-1 sm:px-3">
                        {isOnline ? (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="whitespace-nowrap border-red-200 text-red-700 hover:bg-red-50"
                            data-testid={`admin-catalog-hide-${rowKey}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              void hideDossierFromMarketplace(d.dossier_id);
                            }}
                          >
                            <EyeOff className="mr-1 h-3.5 w-3.5" aria-hidden />
                            Masquer
                          </Button>
                        ) : (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="whitespace-nowrap border-emerald-200 text-emerald-800 hover:bg-emerald-50"
                            data-testid={`admin-catalog-publish-${rowKey}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              void publishDossierToMarketplace(d.dossier_id);
                            }}
                          >
                            <Eye className="mr-1 h-3.5 w-3.5" aria-hidden />
                            En ligne
                          </Button>
                        )}
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="whitespace-nowrap border-slate-300 text-slate-700 hover:bg-slate-50"
                          data-testid={`admin-catalog-delete-${rowKey}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            void deleteDossierPermanently(d.dossier_id);
                          }}
                        >
                          <Trash2 className="mr-1 h-3.5 w-3.5" aria-hidden />
                          Supprimer
                        </Button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </AdminListShell>
              </div>

            {adminPendingDetailId ? (
              <div
                className="relative flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-sm lg:min-h-0"
                data-testid="admin-pending-dossier-panel"
              >
                                <button
                                  type="button"
                  className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-md text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900"
                  onClick={() => setAdminPendingDetailId(null)}
                  aria-label="Fermer la fiche"
                  data-testid="admin-pending-dossier-close"
                >
                  <X className="h-5 w-5" aria-hidden />
                                </button>
                {adminPendingDetailRow ? (
                  <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-slate-100 bg-slate-50/80 px-4 py-3 pr-12">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="truncate text-sm font-semibold text-slate-900">
                          {adminPendingDetailRow.title}
                        </h3>
                        {String(adminPendingDetailRow.status || "").toLowerCase() !== "online" ? (
                          <Badge variant="outline" className="text-[10px] font-normal">
                            {dossierStatusLabelFr(adminPendingDetailRow.status)}
                          </Badge>
                        ) : null}
                      </div>
                      <p className="mt-0.5 truncate text-xs text-slate-600">
                        {adminOwnerLine(ownerDisplay(adminPendingDetailRow))}
                      </p>
                    </div>
                    <div className="flex shrink-0 flex-wrap items-center gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        data-testid="admin-catalog-edit-dossier"
                        onClick={() => void openAdminEditDossier(adminPendingDetailRow.dossier_id)}
                      >
                        <Pencil className="mr-1 h-4 w-4" />
                        Modifier
                      </Button>
                      {String(adminPendingDetailRow.status || "").toLowerCase() === "online" ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="border-red-200 text-red-700 hover:bg-red-50"
                          data-testid="admin-catalog-hide-dossier"
                          onClick={() => void hideDossierFromMarketplace(adminPendingDetailRow.dossier_id)}
                        >
                          <EyeOff className="mr-1 h-4 w-4" aria-hidden />
                          Masquer
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="border-emerald-200 text-emerald-800 hover:bg-emerald-50"
                          data-testid="admin-catalog-publish-dossier"
                          onClick={() => void publishDossierToMarketplace(adminPendingDetailRow.dossier_id)}
                        >
                          <Eye className="mr-1 h-4 w-4" aria-hidden />
                          Remettre en ligne
                        </Button>
                      )}
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="border-slate-300 text-slate-800 hover:bg-slate-50"
                        data-testid="admin-catalog-delete-dossier"
                        onClick={() => void deleteDossierPermanently(adminPendingDetailRow.dossier_id)}
                      >
                        <Trash2 className="mr-1 h-4 w-4" aria-hidden />
                        Supprimer
                      </Button>
                    </div>
                  </div>
                ) : null}
                <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
                  <ModalDossierEmbed
                    key={`${adminPendingDetailId}-${adminDossierEmbedKey}`}
                    dossierId={adminPendingDetailId}
                    onBack={() => setAdminPendingDetailId(null)}
                    hideNavBack
                  />
                                </div>
                              </div>
                                  ) : null}
                                </div>

          </TabsContent>

          <TabsContent
            value="diffusion"
            className="mt-0 space-y-4 focus-visible:outline-none"
            data-testid="admin-diffusion"
          >
            <AdminCatalogBroadcastCard produits={produits} />
          </TabsContent>

          <TabsContent value="disputes" className="mt-0 space-y-4 focus-visible:outline-none">
            <p className="text-sm text-slate-600">
              Litiges <strong>ouverts</strong> en premier (fenêtre {SELLER_PAYOUT_HOURS} h après clôture RDV).
              Cliquez une ligne pour ouvrir le détail dans une fenêtre.
            </p>
            <AdminListShell
              searchId="admin-disputes-search"
              searchPlaceholder="Annonce, motif, acheteur, vendeur…"
              searchValue={adminDisputesQuery}
              onSearchChange={(e) => setAdminDisputesQuery(e.target.value)}
              count={disputes.length === 0 ? 0 : disputesSorted.length}
              total={disputes.length}
              countNoun="litige"
              emptyMessage={
                disputes.length === 0
                  ? "Aucun litige enregistré."
                  : "Aucun litige ne correspond à la recherche."
              }
              listTestId="admin-disputes-list"
              columnGridClassName="sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto_1.25rem]"
              columns={[
                { key: "annonce", label: "Annonce", className: "sm:col-start-1" },
                { key: "motif", label: "Motif / délai", className: "sm:col-start-2" },
                { key: "etat", label: "État", className: "text-center sm:col-start-3" },
                { key: "go", label: "", className: "sm:col-start-4" },
              ]}
            >
                {disputesSorted.map((d) => {
                  const litigePk = d.id || d.dispute_id;
                  const rowKey = String(litigePk);
                  const rawOpen = d.status === "open" || d.status === "in_review";
                  const tempsFenetre = litigeLigneTempsFenetre72h(d);
                  const tempsFenetreClass =
                    tempsFenetre.tone === "remaining"
                    ? "text-orange-800 font-medium"
                      : tempsFenetre.tone === "overdue"
                      ? "text-red-700 font-medium"
                        : "text-slate-500";
                  return (
                  <AdminListRow
                      key={rowKey}
                    testId={`admin-dispute-row-${rowKey}`}
                    gridClassName="sm:grid sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto_1.25rem] sm:items-center sm:gap-2"
                    onClick={() => {
                      setAdminLitigeReturnTo(null);
                      setAdminLitigeDetailId(rowKey);
                    }}
                    className={
                      focusedLitigeId === rowKey ? "bg-sky-50/80 ring-1 ring-inset ring-sky-300" : undefined
                    }
                  >
                    <div className="min-w-0 flex-1 sm:col-span-1">
                      <div className="truncate text-sm font-semibold text-slate-900">
                            {d.dossier?.title || "Annonce"}
                                </div>
                      <div className="text-[11px] text-slate-500 sm:hidden line-clamp-1">
                        {d.motif || d.reason || "—"}
                                  </div>
                                    </div>
                    <div className="hidden min-w-0 sm:block sm:col-span-1">
                      <div className="truncate text-xs text-slate-600">{d.motif || d.reason || "—"}</div>
                      <div className={cn("mt-0.5 text-[11px] tabular-nums", tempsFenetreClass)}>
                            {tempsFenetre.text}
                                </div>
                              </div>
                    <Badge
                      className={cn(
                        "shrink-0 sm:justify-self-center",
                        rawOpen ? "bg-orange-100 text-orange-800" : "bg-slate-200 text-slate-700",
                      )}
                    >
                          {litigeRowStatusLabelFr(d.status)}
                        </Badge>
                  </AdminListRow>
                                );
                              })}
            </AdminListShell>
          </TabsContent>

          <TabsContent value="contact" className="mt-0 space-y-4 focus-visible:outline-none">
            <p className="text-sm text-slate-600">
              Cliquez une ligne pour le détail et répondre par e-mail.
            </p>
            <AdminListShell
              searchId="admin-contact-search"
              searchPlaceholder="E-mail, message, sujet…"
              searchValue={adminSuggestionsQuery}
              onSearchChange={(e) => setAdminSuggestionsQuery(e.target.value)}
              count={suggestions.length === 0 ? 0 : suggestionsFiltered.length}
              total={suggestions.length}
              countNoun="ticket"
              emptyMessage={
                suggestions.length === 0
                  ? "Aucun message pour le moment."
                  : "Aucun message ne correspond à la recherche."
              }
              listTestId="admin-contact-list"
              toolbar={
              <Button
                variant="outline"
                size="sm"
                onClick={loadSuggestions}
                disabled={suggestionsLoading}
                  data-testid="admin-refresh-contact"
              >
                  {suggestionsLoading ? "…" : "Rafraîchir"}
              </Button>
              }
              columnGridClassName="sm:grid-cols-[auto_minmax(0,1fr)_minmax(0,1fr)_1.25rem]"
              columns={[
                { key: "type", label: "Type", className: "sm:col-start-1" },
                { key: "sujet", label: "Sujet", className: "sm:col-start-2" },
                { key: "auteur", label: "Auteur / date", className: "sm:col-start-3" },
                { key: "go", label: "", className: "sm:col-start-4" },
              ]}
            >
              {suggestionsFiltered.map((s) => {
                const rowKey = String(s.id);
                const adminKind = adminSuggestionKind(s);
                  const email = String(s?._admin_display_email || s?.email || "").trim();
                  const displayName = String(s?._admin_display_name || "").trim();
                  const whoBits = [];
                  if (displayName) whoBits.push(displayName);
                  if (email && !whoBits.includes(email)) whoBits.push(email);
                  const whoLine = whoBits.join(" · ");
                  const when = s?.created_at ? new Date(s.created_at).toLocaleString("fr-FR") : "";
                  const products = s?.suggested_products ? String(s.suggested_products) : "";
                const subjectLine =
                  adminKind === "Suggestion" && products
                    ? products
                    : adminKind === "Bug"
                      ? "Bug / dysfonctionnement"
                      : adminKind === "Question"
                        ? "Question"
                        : "—";
                  return (
                  <AdminListRow
                    key={rowKey}
                    testId={`admin-suggestion-row-${rowKey}`}
                    gridClassName="sm:grid sm:grid-cols-[auto_minmax(0,1fr)_minmax(0,1fr)_1.25rem] sm:items-center sm:gap-2"
                    onClick={() => setAdminSuggestionDetailId(rowKey)}
                              className={
                      adminSuggestionDetailId === rowKey
                        ? "bg-sky-50/80 ring-1 ring-inset ring-sky-300"
                        : undefined
                    }
                  >
                    <Badge
                      className={cn(
                        "shrink-0 sm:col-span-1",
                                adminKind === "Question"
                                  ? "bg-blue-100 text-blue-800"
                                  : adminKind === "Bug"
                                    ? "bg-amber-100 text-amber-900"
                            : "bg-emerald-100 text-emerald-800",
                      )}
                            >
                              {adminKind}
                            </Badge>
                    <div className="min-w-0 truncate text-sm font-medium text-slate-900 sm:col-span-1">
                      {subjectLine}
                        </div>
                    <div className="hidden min-w-0 sm:block sm:col-span-1">
                      <div className="truncate text-xs text-slate-600">{whoLine || "—"}</div>
                      <div className="text-[11px] text-slate-500">{when || "—"}</div>
                      </div>
                  </AdminListRow>
                  );
                })}
            </AdminListShell>
          </TabsContent>

          <TabsContent value="transactions" className="mt-0 focus-visible:outline-none">
            <AdminTransactionsTab
              transactions={adminTransactions}
              query={adminTransactionsQuery}
              onQueryChange={setAdminTransactionsQuery}
              filter={adminTxFilter}
              onFilterChange={setAdminTxFilter}
              detailId={adminTransactionDetailId}
              onDetailIdChange={setAdminTransactionDetailId}
              detailCloseLocked={
                Boolean(adminLitigeDetailId) || Boolean(adminSelectedUserId) || Boolean(callRecordingPlayer)
              }
              detailDialogHidden={
                Boolean(adminLitigeDetailId) ||
                Boolean(adminSelectedUserId) ||
                Boolean(callRecordingPlayer)
              }
              onOpenLitige={openAdminLitigeModal}
              onOpenUser={openAdminUserModal}
              onRefresh={load}
              onListenRecording={handleAdminListenRecording}
              recordingLoadTid={recordingLoadTid}
            />
          </TabsContent>

          <TabsContent value="users" className="mt-0 focus-visible:outline-none">
            <p className="mb-3 text-sm text-slate-600">
              Filtrez par résiliation d’abonnement ou comptes supprimés par la modération. Cliquez une ligne pour la
              fiche complète (date de résiliation dans le détail si disponible).
            </p>
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant={adminUsersFilter === "all" ? "default" : "outline"}
                size="sm"
                className={adminUsersFilter === "all" ? "bg-slate-800 hover:bg-slate-900" : ""}
                onClick={() => setAdminUsersFilter("all")}
                data-testid="admin-users-filter-all"
              >
                Comptes actifs
              </Button>
              <Button
                type="button"
                variant={adminUsersFilter === "churned" ? "default" : "outline"}
                size="sm"
                className={adminUsersFilter === "churned" ? "bg-red-700 hover:bg-red-800" : "border-red-200 text-red-800"}
                onClick={() => setAdminUsersFilter("churned")}
                data-testid="admin-users-filter-churned"
              >
                Résiliations abonnement ({adminUsersChurnedCount})
              </Button>
              {adminUsersSuspendedCount > 0 ? (
                <Button
                  type="button"
                  variant={adminUsersFilter === "suspended" ? "default" : "outline"}
                  size="sm"
                  className={adminUsersFilter === "suspended" ? "bg-amber-700 hover:bg-amber-800" : ""}
                  onClick={() => setAdminUsersFilter("suspended")}
                  data-testid="admin-users-filter-suspended"
                >
                  Suspendus ({adminUsersSuspendedCount})
                </Button>
              ) : null}
              {adminDeletedUserCount > 0 ? (
                <Button
                  type="button"
                  variant={adminUsersFilter === "deleted" ? "default" : "outline"}
                  size="sm"
                  className={adminUsersFilter === "deleted" ? "bg-slate-800 hover:bg-slate-900" : ""}
                  onClick={() => setAdminUsersFilter("deleted")}
                  data-testid="admin-users-filter-deleted"
                >
                  Comptes supprimés ({adminDeletedUserCount})
                </Button>
              ) : null}
            </div>
            <AdminListShell
              searchId="admin-user-search"
              searchPlaceholder="Nom, e-mail ou société…"
              searchValue={adminUserQuery}
              onSearchChange={(e) => setAdminUserQuery(e.target.value)}
              searchAriaLabel="Rechercher un utilisateur"
              count={adminUsersFiltered.length}
              total={adminUsersActive.length}
              countNoun="compte"
              emptyMessage={
                adminUsersFilter === "churned"
                  ? "Aucune résiliation d’abonnement enregistrée (ou aucun résultat pour la recherche)."
                  : adminUsersFilter === "deleted"
                    ? "Aucun compte supprimé."
                    : adminUsersFilter === "suspended"
                      ? "Aucun compte suspendu."
                      : "Aucun compte ne correspond à la recherche."
              }
              listTestId="admin-users-list"
              columnGridClassName="grid-cols-[2.5rem_minmax(0,1fr)_auto_1.25rem] sm:grid-cols-[2.5rem_minmax(0,1fr)_minmax(0,1.2fr)_minmax(0,1fr)_auto_1.25rem]"
              columns={[
                { key: "avatar", label: "", className: "col-start-1" },
                { key: "nom", label: "Nom", className: "col-start-2" },
                { key: "email", label: "E-mail", className: "hidden sm:col-start-3 sm:block" },
                { key: "societe", label: "Société", className: "hidden sm:col-start-4 sm:block" },
                { key: "etat", label: "État", className: "col-start-3 text-center sm:col-start-5" },
                { key: "go", label: "", className: "col-start-4 sm:col-start-6" },
              ]}
            >
                          {adminUsersFiltered.map((u) => {
                            const initials = adminUserInitials(u.name);
                const subscriptionStatus = adminUserSubscriptionStatusForList(u);
                                return (
                  <AdminListRow
                    key={u.user_id}
                    testId={`admin-user-row-${u.user_id}`}
                    gridClassName="grid w-full grid-cols-[2.5rem_minmax(0,1fr)_auto_1.25rem] items-center gap-x-2 sm:grid-cols-[2.5rem_minmax(0,1fr)_minmax(0,1.2fr)_minmax(0,1fr)_auto_1.25rem]"
                    chevronClassName="col-start-4 justify-self-end sm:col-start-6"
                                  onClick={() => setAdminSelectedUserId(u.user_id)}
                    className={cn(
                      adminSelectedUserId === u.user_id && "bg-sky-50/80 ring-1 ring-inset ring-sky-300",
                      isAdminProfileDeleted(u) && "opacity-70",
                    )}
                                >
                                  <div
                      className="col-start-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-slate-100 to-slate-200/90 text-[10px] font-bold tracking-tight text-slate-700"
                                    aria-hidden
                                  >
                                    {initials}
                        </div>
                    <div className="col-start-2 min-w-0">
                                    <div className="truncate text-sm font-semibold text-slate-900">{u.name}</div>
                      <div className="truncate text-[11px] text-slate-500 sm:hidden">
                        {[profileRoleLabelFr(u.role), u.email, u.company_name].filter(Boolean).join(" · ")}
                      </div>
                      <div className="hidden truncate text-[11px] text-slate-500 sm:block">
                        {profileRoleLabelFr(u.role)}
                    </div>
                  </div>
                    <div className="hidden min-w-0 truncate text-sm text-slate-600 sm:col-start-3 sm:block">
                      {u.email}
                    </div>
                    <div className="hidden min-w-0 truncate text-sm text-slate-600 sm:col-start-4 sm:block">
                      {u.company_name || "—"}
                  </div>
                    <div
                      className="col-start-3 flex max-w-[9.5rem] shrink-0 justify-end sm:col-start-5 sm:justify-center"
                      title={subscriptionStatus.title}
                    >
                      <Badge
                        className={cn(
                          "shrink-0 text-[10px] px-1.5 py-0 whitespace-nowrap",
                          subscriptionStatus.badgeClass,
                        )}
                      >
                        {subscriptionStatus.label}
                                    </Badge>
              </div>
                  </AdminListRow>
                                );
                              })}
            </AdminListShell>
          </TabsContent>

          <TabsContent value="produits" className="mt-0 space-y-4 focus-visible:outline-none">
                <p className="text-sm text-slate-600">
              Catalogue produits et champs associés. Cliquez une ligne pour le détail, l’édition ou la duplication.
            </p>
            <AdminListShell
              searchId="admin-produits-search"
              searchPlaceholder="Nom, description, nom d’un champ…"
              searchValue={produitSearch}
              onSearchChange={(e) => setProduitSearch(e.target.value)}
              count={produitsFiltres.length}
              total={produits.length}
              countNoun="produit"
              emptyMessage={
                produits.length === 0
                  ? "Aucun produit. Créez-en un avec le bouton ci-contre."
                  : "Aucun produit ne correspond à la recherche ou au secteur."
              }
              listTestId="admin-produits-list"
              toolbar={
                <>
                <Select
                  value={produitSecteurFilter || "all"}
                  onValueChange={(v) => setProduitSecteurFilter(v === "all" ? "" : v)}
                >
                    <SelectTrigger className="h-9 w-[11rem]" data-testid="admin-produits-secteur-filter">
                      <SelectValue placeholder="Secteur" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les secteurs</SelectItem>
                    {secteurs.map((s) => (
                      <SelectItem key={s.secteur_id} value={s.nom}>
                        {s.nom}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                    <Button
                    className="btn-primary shrink-0"
                    onClick={() => {
                      setEditingProduit(null);
                      setNewProd({ nom: "", description: "", secteur: "", champs: [] });
                      setNewChamp({
                        nom: "",
                        type: "text",
                        cible_annonces: "tous",
                        listOptionRows: [""],
                        required: false,
                        visible_public: true,
                      });
                      setShowProdDialog(true);
                    }}
                    data-testid="create-produit-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Nouveau
                    </Button>
                </>
              }
              columnGridClassName="sm:grid-cols-[minmax(0,1fr)_auto_1.25rem]"
              columns={[
                { key: "nom", label: "Produit", className: "sm:col-start-1" },
                { key: "secteur", label: "Secteur", className: "sm:col-start-2" },
                { key: "go", label: "", className: "sm:col-start-3" },
              ]}
            >
              {produitsFiltres.map((pr) => {
                const rowKey = String(pr.produit_id);
                const champsCount = Array.isArray(pr.champs) ? pr.champs.length : 0;
                return (
                  <AdminListRow
                    key={rowKey}
                    testId={`produit-row-${rowKey}`}
                    gridClassName="sm:grid sm:grid-cols-[minmax(0,1fr)_auto_1.25rem] sm:items-center sm:gap-2"
                    onClick={() => setAdminProduitDetailId(rowKey)}
                    className={
                      adminProduitDetailId === rowKey
                        ? "bg-sky-50/80 ring-1 ring-inset ring-sky-300"
                        : undefined
                    }
                  >
                    <div className="min-w-0 sm:col-span-1">
                      <div className="truncate text-sm font-semibold text-slate-900">{pr.nom}</div>
                      <div className="text-[11px] text-slate-500">
                        {champsCount} champ{champsCount === 1 ? "" : "s"}
                        {pr.description ? ` · ${String(pr.description).slice(0, 48)}${String(pr.description).length > 48 ? "…" : ""}` : ""}
                  </div>
                </div>
                    <Badge className="shrink-0 bg-[#2D7FF9]/5 text-[#2D7FF9] sm:col-span-1">{pr.secteur}</Badge>
                  </AdminListRow>
                );
              })}
            </AdminListShell>
          </TabsContent>

          <TabsContent value="secteurs" className="mt-0 space-y-4 focus-visible:outline-none">
            <p className="text-sm text-slate-600">
              Secteurs d’activité du catalogue. Cliquez une ligne pour le détail et les actions.
            </p>
            <AdminListShell
              searchId="admin-secteurs-search"
              searchPlaceholder="Nom du secteur…"
              searchValue={adminSecteursQuery}
              onSearchChange={(e) => setAdminSecteursQuery(e.target.value)}
              count={secteurs.length === 0 ? 0 : secteursFiltered.length}
              total={secteurs.length}
              countNoun="secteur"
              emptyMessage={
                secteurs.length === 0
                  ? "Aucun secteur actif."
                  : "Aucun secteur ne correspond à la recherche."
              }
              listTestId="admin-secteurs-list"
              toolbar={
              <Button
                  className="btn-primary shrink-0"
                onClick={() => {
                  setEditingSec(null);
                    setNewSec({ nom: "" });
                  setShowSecDialog(true);
                }}
                data-testid="create-secteur-btn"
              >
                  <Plus className="w-4 h-4 mr-1" />
                  Nouveau
              </Button>
              }
              columnGridClassName="sm:grid-cols-[minmax(0,1fr)_1.25rem]"
              columns={[
                { key: "nom", label: "Secteur", className: "sm:col-start-1" },
                { key: "go", label: "", className: "sm:col-start-2" },
              ]}
            >
              {secteursFiltered.map((sec) => {
                const rowKey = String(sec.secteur_id);
                return (
                  <AdminListRow
                    key={rowKey}
                    testId={`secteur-row-${rowKey}`}
                    gridClassName="sm:grid sm:grid-cols-[minmax(0,1fr)_1.25rem] sm:items-center sm:gap-2"
                    onClick={() => setAdminSecteurDetailId(rowKey)}
                    className={
                      adminSecteurDetailId === rowKey
                        ? "bg-sky-50/80 ring-1 ring-inset ring-sky-300"
                        : undefined
                    }
                  >
                    <div className="min-w-0 sm:col-span-1">
                      <div className="truncate text-sm font-semibold text-slate-900">{sec.nom}</div>
                  </div>
                  </AdminListRow>
                );
              })}
            </AdminListShell>
          </TabsContent>

        </Tabs>
      </AdminShell>

      {/* SECTEUR DIALOG */}
      <Dialog open={showSecDialog} onOpenChange={setShowSecDialog}>
        <DialogContent data-testid="secteur-dialog">
          <DialogHeader><DialogTitle className="font-display">{editingSec ? "Éditer le secteur" : "Nouveau secteur"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Nom</Label><Input value={newSec.nom} onChange={(e) => setNewSec({ ...newSec, nom: e.target.value })} data-testid="secteur-nom" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSecDialog(false)}>Annuler</Button>
            <Button className="btn-primary" onClick={createSec} data-testid="submit-secteur">{editingSec ? "Enregistrer" : "Créer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showProdDialog} onOpenChange={setShowProdDialog}>
        <DialogContent
          className="max-h-[min(90dvh,56rem)] flex max-w-3xl flex-col gap-0 overflow-hidden p-0 sm:max-w-3xl"
          data-testid="create-produit-dialog"
        >
          <div className="shrink-0 border-b border-slate-100 px-6 py-4 pr-12">
            <DialogHeader className="p-0">
              <DialogTitle className="font-display">
                {editingProduit ? "Éditer le produit" : "Nouveau produit"}
              </DialogTitle>
              {!editingProduit && (
                <p className="mt-1 text-sm text-slate-500">Champs du formulaire d’annonce et texte de précisions.</p>
              )}
            </DialogHeader>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain px-6 py-4">
            <div className="space-y-5">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="produit-nom-input">Nom</Label>
                <Input id="produit-nom-input" value={newProd.nom} onChange={(e) => setNewProd({ ...newProd, nom: e.target.value })} data-testid="produit-nom" />
              </div>
              <div className="space-y-1.5">
                <Label>Secteur</Label>
                <Select value={newProd.secteur} onValueChange={(v) => setNewProd({ ...newProd, secteur: v })}>
                  <SelectTrigger data-testid="produit-secteur-select"><SelectValue placeholder="Choisir" /></SelectTrigger>
                  <SelectContent>
                    {secteurs.map((s) => <SelectItem key={s.secteur_id} value={s.nom}>{s.nom}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="border-t border-[#E5E7EB] pt-5">
              <h3 className="text-sm font-medium text-slate-900">Champs dynamiques</h3>
              <div className="mt-3 space-y-3 rounded-lg border border-slate-200 bg-slate-50/60 p-4">
                <p className="text-xs font-medium text-slate-600">Ajouter un champ</p>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5 sm:col-span-2">
                    <Label className="text-xs text-slate-600">Libellé</Label>
                    <Input placeholder="Ex. Puissance, Surface…" value={newChamp.nom} onChange={(e) => setNewChamp({ ...newChamp, nom: e.target.value })} />
                </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-600">Type</Label>
                <Select
                  value={newChamp.type}
                  onValueChange={(v) =>
                    setNewChamp((prev) => ({
                      ...prev,
                      type: v,
                      listOptionRows:
                        v === "list" && (!Array.isArray(prev.listOptionRows) || prev.listOptionRows.length === 0)
                          ? [""]
                          : prev.listOptionRows,
                    }))
                  }
                >
                      <SelectTrigger className="w-full" data-testid="new-champ-type"><SelectValue placeholder="Type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="text">Texte</SelectItem>
                    <SelectItem value="number">Nombre</SelectItem>
                        <SelectItem value="list">Liste déroulante</SelectItem>
                    <SelectItem value="boolean">Oui / Non</SelectItem>
                  </SelectContent>
                </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-600">Annonces concernées</Label>
                <Select
                  value={newChamp.cible_annonces}
                  onValueChange={(v) => setNewChamp({ ...newChamp, cible_annonces: v })}
                >
                      <SelectTrigger className="w-full" data-testid="new-champ-cible-annonces">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CIBLES_ANNONCE.map((x) => (
                      <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-slate-700">
                  {newChamp.type !== "list" && (
                    <label className="flex items-center gap-2">
                    <input type="checkbox" checked={newChamp.required} onChange={(e) => setNewChamp({ ...newChamp, required: e.target.checked })} />
                      Obligatoire
                  </label>
                  )}
                  <label
                    className="flex items-start gap-2"
                    title="Décoché : visible seulement pour le vendeur, masqué sur la fiche publique"
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={newChamp.visible_public !== false}
                      onChange={(e) => setNewChamp({ ...newChamp, visible_public: e.target.checked })}
                      data-testid="new-champ-visible-public"
                    />
                    <span>Visible sur la fiche publique</span>
                  </label>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-9 w-full gap-1.5 border-slate-200 bg-white text-slate-700 hover:bg-white sm:w-auto"
                  onClick={addChamp}
                  data-testid="add-champ"
                >
                  <Plus className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  Ajouter le champ
                </Button>
              </div>
              {newChamp.type === "list" && (
                <div className="mt-3 space-y-2 rounded-md border border-[#E5E7EB] bg-white p-3">
                  <Label className="text-xs text-slate-600">Options de la liste</Label>
                  {(newChamp.listOptionRows || [""]).map((row, j) => (
                    <div key={`new-opt-${j}`} className="flex gap-2 items-center">
                      <Input
                        className="flex-1 h-9 text-sm"
                        placeholder={`Option ${j + 1}`}
                        value={row}
                        onChange={(e) => {
                          const next = [...(newChamp.listOptionRows || [""])];
                          next[j] = e.target.value;
                          setNewChamp({ ...newChamp, listOptionRows: next });
                        }}
                        data-testid={`new-champ-option-${j}`}
                      />
                      {(newChamp.listOptionRows || [""]).length > 1 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="h-9 w-9 shrink-0 text-red-600"
                          onClick={() => {
                            const rows = [...(newChamp.listOptionRows || [""])].filter((_, k) => k !== j);
                            setNewChamp({ ...newChamp, listOptionRows: rows.length ? rows : [""] });
                          }}
                          title="Supprimer cette option"
                          aria-label="Supprimer cette option"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-full sm:w-auto"
                    onClick={() =>
                      setNewChamp((prev) => ({
                        ...prev,
                        listOptionRows: [...(prev.listOptionRows || [""]), ""],
                      }))
                    }
                    data-testid="new-champ-add-option"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Ajouter une option
                  </Button>
                  <label className="flex items-center gap-2 text-sm text-slate-700 pt-1">
                    <input type="checkbox" checked={newChamp.required} onChange={(e) => setNewChamp({ ...newChamp, required: e.target.checked })} />
                    Obligatoire
                  </label>
                </div>
              )}
              {newProd.champs.length > 0 && (
                <p className="text-xs text-slate-500">Glissez ⋮⋮ pour réordonner les champs.</p>
              )}
              <ul className="mt-2 space-y-3" data-testid="produit-champs-order-list">
                {newProd.champs.map((c, i) => {
                  const listEditRows = c.type === "list" ? listOptionsToRows(c.options) : [];
                  return (
                  <li
                    key={c.champ_id != null ? `id-${c.champ_id}` : `new-${i}`}
                    onDragOver={(e) => onChampDragOver(e, i)}
                    onDrop={(e) => onChampDrop(e, i)}
                    className={[
                      "flex items-stretch gap-1.5 rounded-md border bg-slate-50/80 p-1.5 transition-[box-shadow,opacity] border-[#E5E7EB]",
                      champDropOver === i && champDragIndex !== i ? "ring-2 ring-inset ring-[#2D7FF9]/45" : "",
                      champDragIndex === i ? "opacity-50" : "",
                    ].join(" ")}
                  >
                    <div className="flex items-center border-r border-[#E5E7EB] pr-0.5 self-stretch">
                      <div
                        className="cursor-grab active:cursor-grabbing touch-none p-1.5 text-slate-500 hover:text-[#0B0D12] self-center select-none rounded hover:bg-white/80"
                        draggable
                        onDragStart={(e) => onChampDragStart(e, i)}
                        onDragEnd={onChampDragEnd}
                        title="Glisser-déposer pour réordonner"
                        aria-label="Déplacer le champ (glisser-déposer)"
                        data-testid={`champ-drag-${i}`}
                      >
                        <GripVertical className="h-5 w-5" aria-hidden />
                      </div>
                    </div>
                    <div className="min-w-0 flex-1 space-y-2 py-0.5 pl-0.5">
                      <div className="flex items-center gap-1.5 min-w-0 text-xs text-slate-500">
                        <span className="shrink-0">Champ {i + 1}</span>
                        {c.visible_public === false && (
                          <span className="shrink-0 rounded border border-amber-200 bg-amber-50 px-1.5 text-[9px] font-bold uppercase text-amber-800">
                            Masqué
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        <div className="space-y-1 sm:col-span-2">
                          <Label className="text-xs text-slate-600">Libellé</Label>
                        <Input
                          className="h-9 text-sm"
                          value={c.nom}
                          placeholder="Libellé du champ"
                          onChange={(e) => setChampAt(i, { nom: e.target.value })}
                          data-testid={`champ-nom-${i}`}
                        />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs text-slate-600">Type</Label>
                        <Select
                          value={c.type}
                          onValueChange={(v) => setChampAt(i, { type: v })}
                        >
                          <SelectTrigger className="h-9" data-testid={`champ-type-${i}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {PRODUIT_CHAMP_TYPES.map((t) => (
                              <SelectItem key={t} value={t}>
                                {labelProduitChampType(t)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        </div>
                        <div className="space-y-1 sm:col-span-2">
                          <Label className="text-xs text-slate-600">Annonces</Label>
                        <Select
                          value={c.cible_annonces && CIBLES_ANNONCE.some((x) => x.value === c.cible_annonces) ? c.cible_annonces : "tous"}
                          onValueChange={(v) => setChampAt(i, { cible_annonces: v })}
                        >
                            <SelectTrigger className="h-9 text-left" data-testid={`champ-cible-annonces-${i}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {CIBLES_ANNONCE.map((x) => (
                              <SelectItem key={x.value} value={x.value}>{x.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        </div>
                      </div>
                      {c.type === "list" ? (
                        <div className="space-y-2 rounded-md border border-[#E5E7EB] bg-white p-3">
                          <Label className="text-xs text-slate-600">Options du menu</Label>
                          {listEditRows.map((row, j) => (
                            <div key={`${i}-opt-${j}`} className="flex gap-2 items-center">
                              <Input
                                className="flex-1 h-9 text-sm"
                                placeholder={`Option ${j + 1}`}
                                value={row}
                                onChange={(e) => setListOptionCell(i, j, e.target.value)}
                                data-testid={`champ-options-${i}-${j}`}
                              />
                              {listEditRows.length > 1 && (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="icon"
                                  className="h-9 w-9 shrink-0 text-red-600"
                                  onClick={() => removeListOptionCell(i, j)}
                                  title="Supprimer cette option"
                                  aria-label="Supprimer cette option"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          ))}
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="w-full sm:w-auto"
                            onClick={() => addListOptionCell(i)}
                            data-testid={`champ-add-option-${i}`}
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Ajouter une option
                          </Button>
                        </div>
                      ) : null}
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-700">
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={Boolean(c.required)}
                            onChange={(e) => setChampAt(i, { required: e.target.checked })}
                            data-testid={`champ-required-${i}`}
                          />
                          <span>Obligatoire</span>
                        </label>
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={c.visible_public !== false}
                            onChange={(e) => setChampAt(i, { visible_public: e.target.checked })}
                            data-testid={`champ-visible-${i}`}
                          />
                          <span>Visible publique</span>
                        </label>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="self-start p-1.5 text-slate-500 hover:text-red-600 shrink-0"
                      onClick={() => setNewProd((prev) => ({ ...prev, champs: prev.champs.filter((_, j) => j !== i) }))}
                      title="Supprimer le champ"
                      data-testid={`champ-remove-${i}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </li>
                  );
                })}
              </ul>
            </div>
            <div className="border-t border-[#E5E7EB] pt-5">
              <div className="space-y-1.5">
              <Label>Précisions</Label>
                <p className="text-xs text-slate-500">Texte sous les champs (fiche publique). Pas de coordonnées ni liens.</p>
              </div>
              <Textarea
                rows={2}
                value={newProd.description}
                onChange={(e) => setNewProd({ ...newProd, description: e.target.value })}
                onPaste={(e) => {
                  const t = (e.clipboardData || window.clipboardData)?.getData("text/plain");
                  if (t != null && String(t).length > 0 && texteContientCoordonnees(t)) {
                    e.preventDefault();
                    toast.error(messageCoordonneesInterdites);
                  }
                }}
                onDrop={(e) => {
                  const t = e.dataTransfer?.getData("text/plain");
                  if (t != null && String(t) !== "" && texteContientCoordonnees(t)) {
                    e.preventDefault();
                    toast.error(messageCoordonneesInterdites);
                  }
                }}
                data-testid="produit-description"
                className="mt-1"
                placeholder="Décrivez l’offre, les cas d’usage…"
              />
            </div>
          </div>
          </div>
          <div className="shrink-0 border-t border-[#E5E7EB] bg-background px-6 py-4">
            <DialogFooter className="gap-2 p-0 sm:justify-end">
            <Button variant="outline" onClick={() => setShowProdDialog(false)}>Annuler</Button>
            <Button className="btn-primary" onClick={createProd} data-testid="submit-produit">{editingProduit ? "Enregistrer" : "Créer"}</Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        modal
        open={!!callRecordingPlayer}
        onOpenChange={(open) => {
          if (!open) closeCallRecordingPlayer();
        }}
      >
        <DialogContent
          data-testid="admin-call-recording-dialog"
          className="max-w-md"
          overlayClassName="z-[120]"
          wrapperClassName="z-[120]"
          onCloseAutoFocus={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="font-display">Enregistrement d’appel</DialogTitle>
            <DialogDescription className="text-left text-sm text-slate-600">
              {callRecordingPlayer?.transactionId
                ? `Transaction ${callRecordingPlayer.transactionId}${
                    callRecordingPlayer.size
                      ? ` · ${(callRecordingPlayer.size / 1024).toFixed(0)} Ko`
                      : ""
                  }`
                : "Lecture sécurisée via le serveur (jeton admin)."}
            </DialogDescription>
          </DialogHeader>
          {callRecordingPlayer?.url ? (
            <div className="space-y-3 py-1">
              <audio
                controls
                className="w-full"
                preload="auto"
                src={callRecordingPlayer.url}
                onError={() =>
                  setCallRecordingPlayError(
                    "Lecture impossible dans le navigateur (format ou fichier corrompu). Téléchargez le fichier ci-dessous.",
                  )
                }
              >
                Votre navigateur ne permet pas la lecture audio intégrée.
              </audio>
              {callRecordingPlayError ? (
                <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                  {callRecordingPlayError}
                </p>
              ) : null}
              <a
                href={callRecordingPlayer.url}
                download={`appel-${callRecordingPlayer.transactionId}.${
                  String(callRecordingPlayer.mime || "").includes("wav") ? "wav" : "mp3"
                }`}
                className="inline-flex text-sm font-semibold text-[#2D7FF9] hover:underline"
                data-testid="admin-call-recording-download"
              >
                Télécharger l’enregistrement
              </a>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={!!disputeResolveDraft} onOpenChange={(open) => !open && setDisputeResolveDraft(null)}>
        <DialogContent data-testid="admin-resolve-dispute-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">Clôturer le litige</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2 text-sm text-slate-600">
            <p>
              {disputeResolveDraft?.decision === "refund"
                ? "Vous validez un remboursement pour l’acheteur : l’argent lui est rendu ; le vendeur ne touche pas le montant de cette vente."
                : "Vous validez le versement au vendeur : il reçoit les fonds liés à cette vente ; l’acheteur n’est pas remboursé."}
            </p>
            <div>
              <Label htmlFor="admin-resolution-msg">Message à la personne qui ne gagne pas (recommandé)</Label>
              <Textarea
                id="admin-resolution-msg"
                rows={5}
                className="mt-1"
                placeholder="Expliquez les motifs de la décision. Ce texte sera visible sur sa ligne « Mes achats » ou « Mes ventes » (bouton Décision litige)."
                value={disputeResolveDraft?.message ?? ""}
                onChange={(e) =>
                  setDisputeResolveDraft((prev) => (prev ? { ...prev, message: e.target.value } : prev))
                }
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDisputeResolveDraft(null)}>
              Annuler
            </Button>
            <Button
              className="btn-primary"
              onClick={() => void confirmResolveDispute()}
              data-testid="admin-confirm-resolve-dispute"
            >
              Valider la décision
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <AdminDetailDialog
        open={Boolean(adminSelectedUserId)}
        onOpenChange={(open) => {
          if (!open) {
            const returnTo = adminUserReturnTo;
            setAdminSelectedUserId(null);
            setAdminUserDetail(null);
            setAdminUserDetailError("");
            setAdminUserPane("main");
            setAdminUserPaneId(null);
            setAdminUserReturnTo(null);
            if (returnTo?.type === "transaction" && returnTo.transactionId) {
              setAdminTransactionDetailId(String(returnTo.transactionId));
            }
          }
        }}
        onBack={
          adminUserPane !== "main"
            ? () => {
                setAdminUserPane("main");
                setAdminUserPaneId(null);
              }
            : adminUserReturnTo?.type === "transaction"
              ? () => {
                  const txId = String(adminUserReturnTo.transactionId || "").trim();
                  setAdminSelectedUserId(null);
                  setAdminUserDetail(null);
                  setAdminUserDetailError("");
                  setAdminUserPane("main");
                  setAdminUserPaneId(null);
                  setAdminUserReturnTo(null);
                  if (txId) setAdminTransactionDetailId(txId);
                }
              : undefined
        }
        backLabel={
          adminUserPane !== "main"
            ? "Retour"
            : adminUserReturnTo?.type === "transaction"
              ? "Transaction"
              : "Retour"
        }
        size="xl"
        testId="admin-user-detail-dialog"
        title={
          adminUserPane === "dossier"
            ? "Fiche annonce"
            : adminUserPane === "litige"
              ? "Litige"
              : adminUserDetail?.summary?.name || "Utilisateur"
        }
        description={
          adminUserPane === "main" ? adminUserDetail?.summary?.email || undefined : undefined
        }
        loading={adminUserPane === "main" && adminUserDetailLoading}
        error={adminUserPane === "main" ? adminUserDetailError : ""}
        toolbar={
          adminUserPane === "main" && adminUserDetail?.summary ? (
            <AdminUserDetailToolbar
              summary={adminUserDetail.summary}
              userId={adminSelectedUserId}
              onAccountAction={adminUserAccountAction}
            />
          ) : null
        }
      >
        {adminUserPane === "dossier" && adminUserPaneId ? (
          <ModalDossierEmbed
            dossierId={adminUserPaneId}
            onBack={() => {
              setAdminUserPane("main");
              setAdminUserPaneId(null);
            }}
          />
        ) : adminUserPane === "litige" && adminUserPaneId ? (
          adminUserLitigeRow ? (
              <AdminLitigeExpandedBody
              d={adminUserLitigeRow}
                recordingLoadTid={recordingLoadTid}
                onListenRecording={handleAdminListenRecording}
                onOpenRefund={(id) => setDisputeResolveDraft({ id, decision: "refund", message: "" })}
                onOpenRelease={(id) => setDisputeResolveDraft({ id, decision: "release", message: "" })}
              onViewDossier={(id) => {
                setAdminUserPane("dossier");
                setAdminUserPaneId(id);
              }}
                sheet
            />
          ) : (
            <p className="p-4 text-sm text-slate-600">Litige introuvable dans la liste chargée.</p>
          )
        ) : adminUserDetail ? (
          <AdminUserDetailPanel
            detail={adminUserDetail}
            userId={adminSelectedUserId}
            sellerQuality={adminSellerQuality}
            buyerQuality={adminBuyerQuality}
            formatAdminDateTime={formatAdminDateTime}
            onOpenDossier={(id) => {
              setAdminUserPane("dossier");
              setAdminUserPaneId(id);
            }}
            onOpenLitige={(id) => {
              setAdminUserPane("litige");
              setAdminUserPaneId(id);
            }}
            onOpenUser={openAdminUserPeer}
            onActivityClick={openAdminActivityRow}
          />
        ) : null}
      </AdminDetailDialog>
      <AdminDetailDialog
        open={Boolean(adminLitigeDetailId)}
        onOpenChange={(open) => {
          if (!open && callRecordingPlayer) return;
          if (!open) closeAdminLitigeModal();
        }}
        onBack={
          adminLitigePane === "dossier" || adminLitigeReturnTo?.type === "transaction"
            ? handleAdminLitigeBack
            : undefined
        }
        backLabel={
          adminLitigePane === "dossier"
            ? "Retour"
            : adminLitigeReturnTo?.type === "transaction"
              ? "Transaction"
              : "Retour"
        }
        size="xl"
        testId="admin-litige-detail-dialog"
        title={
          adminLitigePane === "dossier"
            ? "Fiche annonce"
            : adminLitigeDetailRow?.dossier?.title
              ? `${adminLitigeDetailRow.dossier.title} — Litige`
              : "Détail du litige"
        }
        description={
          adminLitigePane === "main" && adminLitigeDetailRow
            ? `${litigeRowStatusLabelFr(adminLitigeDetailRow.status)} · ${adminLitigeDetailRow.motif || adminLitigeDetailRow.reason || "Sans motif"}`
            : undefined
        }
      >
        {adminLitigePane === "dossier" && adminLitigePaneDossierId ? (
          <ModalDossierEmbed
            dossierId={adminLitigePaneDossierId}
            onBack={() => {
              setAdminLitigePane("main");
              setAdminLitigePaneDossierId(null);
            }}
          />
        ) : adminLitigeDetailRow ? (
          <AdminLitigeExpandedBody
            d={adminLitigeDetailRow}
            recordingLoadTid={recordingLoadTid}
            onListenRecording={handleAdminListenRecording}
            onOpenRefund={(id) => setDisputeResolveDraft({ id, decision: "refund", message: "" })}
            onOpenRelease={(id) => setDisputeResolveDraft({ id, decision: "release", message: "" })}
            onViewDossier={(id) => {
              setAdminLitigePane("dossier");
              setAdminLitigePaneDossierId(id);
            }}
            sheet={false}
              />
            ) : (
              <div className="space-y-3 text-sm text-slate-600">
                <p>Ce litige est introuvable dans la liste chargée.</p>
                <Button type="button" variant="outline" size="sm" onClick={() => void load()}>
                  Recharger les données
                </Button>
              </div>
            )}

      </AdminDetailDialog>

      <AdminDetailDialog
        open={Boolean(adminSuggestionDetailId)}
        onOpenChange={(open) => {
          if (!open) setAdminSuggestionDetailId(null);
        }}
        testId="admin-suggestion-detail-dialog"
        title={
          adminSuggestionDetailRow
            ? `${adminSuggestionKind(adminSuggestionDetailRow)} — contact`
            : "Contact"
        }
        description={
          adminSuggestionDetailRow?.created_at
            ? new Date(adminSuggestionDetailRow.created_at).toLocaleString("fr-FR")
            : undefined
        }
      >
        {adminSuggestionDetailRow ? (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <Badge
                className={
                  adminSuggestionKind(adminSuggestionDetailRow) === "Question"
                    ? "bg-blue-100 text-blue-800"
                    : adminSuggestionKind(adminSuggestionDetailRow) === "Bug"
                      ? "bg-amber-100 text-amber-900"
                      : "bg-emerald-100 text-emerald-800"
                }
              >
                {adminSuggestionKind(adminSuggestionDetailRow)}
              </Badge>
              {adminSuggestionDetailRow.context ? (
                <Badge variant="outline">{adminSuggestionDetailRow.context}</Badge>
              ) : null}
          </div>
            {adminSuggestionDetailRow.suggested_products ? (
              <p className="text-sm font-semibold text-slate-900">
                {String(adminSuggestionDetailRow.suggested_products)}
              </p>
            ) : null}
            <p className="text-xs text-slate-600">
              {String(adminSuggestionDetailRow._admin_display_name || "").trim() ||
                String(adminSuggestionDetailRow._admin_display_email || adminSuggestionDetailRow.email || "—")}
            </p>
            {adminSuggestionDetailRow.message ? (
              <div className="rounded-lg border border-slate-200 bg-slate-50/80 p-3 text-sm text-slate-700 whitespace-pre-wrap">
                {String(adminSuggestionDetailRow.message)}
    </div>
            ) : null}
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                const mailto = buildAdminSuggestionMailto(adminSuggestionDetailRow);
                if (mailto) window.location.href = mailto;
              }}
              disabled={!buildAdminSuggestionMailto(adminSuggestionDetailRow)}
              data-testid={`admin-reply-suggestion-${adminSuggestionDetailRow.id}`}
            >
              <Mail className="w-4 h-4 mr-1" />
              Répondre par e-mail
            </Button>
          </div>
        ) : null}
      </AdminDetailDialog>
      <AdminDetailDialog
        open={Boolean(adminProduitDetailId)}
        onOpenChange={(open) => {
          if (!open) setAdminProduitDetailId(null);
        }}
        size="xl"
        testId="admin-produit-detail-dialog"
        title={adminProduitDetailRow?.nom || "Produit"}
        description={adminProduitDetailRow?.secteur}
      >
        {adminProduitDetailRow ? (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">{adminProduitDetailRow.description || "—"}</p>
            {Array.isArray(adminProduitDetailRow.champs) && adminProduitDetailRow.champs.length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {adminProduitDetailRow.champs.map((c) => (
                  <span key={c.champ_id} className="text-xs px-2 py-0.5 bg-slate-100 rounded">
                    {c.nom}
                    {c.visible_public === false ? (
                      <span className="ml-0.5 text-amber-700/90">(masqué)</span>
                    ) : null}
                    <span className="text-slate-400">
                      ({labelProduitChampType(c.type)}
                      {c.required ? "*" : ""})
                    </span>
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-xs text-slate-500">Aucun champ défini.</p>
            )}
            <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-4">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  duplicateProduit(adminProduitDetailRow);
                  setAdminProduitDetailId(null);
                }}
                data-testid={`duplicate-produit-${adminProduitDetailRow.produit_id}`}
              >
                <Copy className="w-4 h-4 mr-1" />
                Dupliquer
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  openEditProduit(adminProduitDetailRow);
                  setAdminProduitDetailId(null);
                }}
                data-testid={`edit-produit-${adminProduitDetailRow.produit_id}`}
              >
                Éditer
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="text-red-600 hover:text-red-700"
                onClick={() => {
                  deleteProd(adminProduitDetailRow.produit_id);
                  setAdminProduitDetailId(null);
                }}
                data-testid={`delete-produit-${adminProduitDetailRow.produit_id}`}
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Supprimer
              </Button>
            </div>
          </div>
        ) : null}
      </AdminDetailDialog>
      <AdminDetailDialog
        open={Boolean(adminSecteurDetailId)}
        onOpenChange={(open) => {
          if (!open) setAdminSecteurDetailId(null);
        }}
        testId="admin-secteur-detail-dialog"
        title={adminSecteurDetailRow?.nom || "Secteur"}
      >
        {adminSecteurDetailRow ? (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  openEditSec(adminSecteurDetailRow);
                  setAdminSecteurDetailId(null);
                }}
                data-testid={`edit-secteur-${adminSecteurDetailRow.secteur_id}`}
              >
                Éditer
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="text-red-600"
                onClick={() => {
                  deleteSec(adminSecteurDetailRow.secteur_id);
                  setAdminSecteurDetailId(null);
                }}
                data-testid={`delete-secteur-${adminSecteurDetailRow.secteur_id}`}
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Supprimer
              </Button>
            </div>
          </div>
        ) : null}
      </AdminDetailDialog>

      <AnnonceEditDialog
        open={Boolean(adminEditDossier)}
        dossier={adminEditDossier}
        onOpenChange={(open) => {
          if (!open) setAdminEditDossier(null);
        }}
        title="Modifier l’annonce (admin)"
        savePut={(id, payload) => api.put(`/admin/dossiers/${id}`, payload)}
        onSaved={async () => {
          setAdminDossierEmbedKey((k) => k + 1);
          await load();
        }}
      />
    </>
  );
};

export default Admin;
